import React, { useEffect, useState } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  updateItemQuantity,
  addToCart,
  fetchCart,
} from "../../../redux/thunks/cartThunks";
import {
  fetchWishlist,
  addItemToWishlist,
  removeItemFromWishlist,
} from "../../../redux/thunks/wishlistThunks";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import {
  AiOutlineHeart,
  AiFillHeart,
  AiOutlineShoppingCart,
  AiOutlineLeft,
  AiOutlineRight,
} from "react-icons/ai";
import { BsStarFill, BsStarHalf, BsStar, BsStars } from "react-icons/bs";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { PulseLoader } from "react-spinners";
import fallbackImage from "../../../assets/brand-default.jpg";
import { globalLogout } from "../../../Utils/Logout";
const TopRatedProducts = () => {
  const [products, setProducts] = useState([]);
  const [topRatedProducts, setTopRatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loadingWishlistItems, setLoadingWishlistItems] = useState({});
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [productToAdd, setProductToAdd] = useState(null);
  const [imageErrors, setImageErrors] = useState({});
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth >= 768 && window.innerWidth < 1024);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const token = Cookies.get("ecom_user_token");
  const cartItems = useSelector((state) => state.cart.items);
  const { items: wishlist, loading: wishlistLoading } = useSelector(
    (state) => state.wishlist
  );

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      setIsTablet(window.innerWidth >= 768 && window.innerWidth < 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Custom arrow components with responsive adjustments
  const NextArrow = (props) => {
    const { className, style, onClick } = props;
    return (
      <div
        className={`${className} custom-slick-arrow`}
        style={{
          ...style,
          display: "block",
          right: isMobile ? "-10px" : isTablet ? "-15px" : "-25px",
          width: isMobile ? "30px" : "40px",
          height: isMobile ? "30px" : "40px",
          zIndex: 1,
        }}
        onClick={onClick}
      >
        <div className="w-full h-full bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-blue-50 transition-colors duration-200 border border-gray-200">
          <AiOutlineRight className="text-gray-700 text-lg md:text-xl" />
        </div>
      </div>
    );
  };

  const PrevArrow = (props) => {
    const { className, style, onClick } = props;
    return (
      <div
        className={`${className} custom-slick-arrow`}
        style={{
          ...style,
          display: "block",
          left: isMobile ? "-10px" : isTablet ? "-15px" : "-25px",
          width: isMobile ? "30px" : "40px",
          height: isMobile ? "30px" : "40px",
          zIndex: 1,
        }}
        onClick={onClick}
      >
        <div className="w-full h-full bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-blue-50 transition-colors duration-200 border border-gray-200">
          <AiOutlineLeft className="text-gray-700 text-lg md:text-xl" />
        </div>
      </div>
    );
  };

  // Slider settings with improved responsive breakpoints
  const sliderSettings = {
    dots: !isMobile, // Hide dots on mobile
    infinite: true,
    speed: 500,
    slidesToShow: isMobile ? 1 : isTablet ? 2 : 4,
    slidesToScroll: 1,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
          infinite: true,
          dots: true,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          dots: false,
          arrows: !isMobile,
        },
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          dots: false,
          arrows: false,
        },
      },
    ],
  };

  useEffect(() => {
    if (token) {
      dispatch(fetchWishlist());
    }
  }, [token, dispatch]);

  useEffect(() => {
    dispatch(fetchCart());
  }, [dispatch]);

  const handleImageError = (productId) => {
    setImageErrors((prev) => ({
      ...prev,
      [productId]: true,
    }));
  };

  const getProductImage = (product) => {
    if (imageErrors[product._id]) {
      return fallbackImage;
    }
    return (
      product.variants?.[0]?.images?.[0] || product.images?.[0] || fallbackImage
    );
  };

  useEffect(() => {
    const fetchTopRatedProducts = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/seller/product/get-by-rating`
        );

        // Process products to find active variants only
        const processedProducts = response?.data?.products
          ?.map((item) => {
            const product = item.product;

            // Filter out non-active variants
            const activeVariants = product.variants?.filter(
              (v) => v.status === "active"
            );

            if (activeVariants.length === 0) return null; // Skip products with no active variants

            // Create a new product object with only active variants
            return {
              ...item,
              product: {
                ...product,
                variants: activeVariants,
                // Add a flag to indicate if the product is in stock
                isInStock: activeVariants.some(
                  (v) => v.stock > 0 || v.stock === undefined
                ),
              },
            };
          })
          .filter(Boolean); // Remove null entries

        // Sort products by rating (highest first) and then by number of ratings
        const sortedProducts = processedProducts.sort((a, b) => {
          if (b.averageRating !== a.averageRating) {
            return b.averageRating - a.averageRating;
          }
          return b.ratingCount - a.ratingCount;
        });

        setProducts(sortedProducts);
        // Take top 5 products for the slider
        setTopRatedProducts(sortedProducts.slice(0, 5));
        setLoading(false);
      } catch (err) {
        globalLogout(err)
        setError(err.message);
        setLoading(false);
        console.error("Error fetching top rated products:", err);
      }
    };

    fetchTopRatedProducts();
  }, []);

  const fetchProductDetails = (productId) => {
    navigate(`/product/${productId}`);
  };

  const handleAddToCart = async (product, e) => {
    e.stopPropagation();

    if (!token) {
      setProductToAdd(product);
      setShowLoginModal(true);
      return;
    }

    const firstVariantId = product.variants?.[0]?._id;

    if (!product.isInStock) {
      toast.warning("This item is out of stock");
      return;
    }

    try {
      await dispatch(
        addToCart({
          productId: product._id,
          variantId: product.variants?.length > 0 ? firstVariantId : undefined,
          quantity: 1,
        })
      );
      toast.success("Added to cart!");
    } catch (error) {
      console.error("Add to cart failed:", error);
      toast.error(error.response?.data?.message || "Failed to add to cart");
    }
  };

  const confirmAddToCart = async () => {
    setShowLoginModal(false);
    navigate("/login", { state: { from: window.location.pathname } });
  };

  const handleQuantityChange = async (productId, newQuantity, e) => {
    e.stopPropagation();
    const product = products.find((p) => p.product._id === productId)?.product;
    const firstVariantId = product?.variants?.[0]?._id;

    try {
      await dispatch(
        updateItemQuantity({
          productId,
          variantId: product?.variants?.length > 0 ? firstVariantId : undefined,
          quantity: newQuantity,
        })
      );
    } catch (error) {
      console.error("Error updating quantity:", error);
      toast.error("Failed to update quantity. Please try again.");
    }
  };

  const toggleWishlist = async (productId, variantId, e) => {
    e.stopPropagation();
    if (!token) {
      toast.info("Please login to manage your wishlist");
      navigate("/login");
      return;
    }

    try {
      setLoadingWishlistItems((prev) => ({ ...prev, [productId]: true }));

      const isInWishlist = wishlist.some(
        (item) =>
          item.productId._id === productId && item.variantId === variantId
      );

      if (isInWishlist) {
        await dispatch(removeItemFromWishlist(productId, variantId));
        toast.success("Removed from wishlist");
      } else {
        await dispatch(addItemToWishlist(productId, variantId));
        toast.success("Added to wishlist");
      }
    } catch (error) {
      console.error("Wishlist operation failed:", error);
      toast.error(
        error.response?.data?.message ||
          "Failed to update wishlist. Please try again."
      );
    } finally {
      setLoadingWishlistItems((prev) => ({ ...prev, [productId]: false }));
    }
  };

  const renderProductActions = (product) => {
    const firstVariant = product.variants?.[0];
    const firstVariantId = firstVariant?._id;

    if (!product.isInStock) {
      return (
        <button
          className="px-3 py-1.5 text-xs md:px-4 md:py-2 md:text-sm rounded-full bg-gray-300 text-gray-600 cursor-not-allowed font-medium"
          disabled
        >
          Out of Stock
        </button>
      );
    }

    const cartItem = cartItems.find(
      (item) =>
        item.productId === product._id &&
        (product.variants?.length > 0
          ? item.variantId === firstVariantId
          : true)
    );

    if (cartItem) {
      return (
        <div className="flex items-center justify-center space-x-1 md:space-x-2">
          <button
            className={`w-6 h-6 md:w-8 md:h-8 flex items-center justify-center rounded-full transition-all duration-200 shadow-sm ${
              cartItem.quantity <= 0
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-gradient-to-b from-gray-100 to-gray-200 hover:from-gray-200 hover:to-gray-300 text-gray-700 hover:text-gray-900 hover:shadow-md"
            }`}
            onClick={(e) => {
              const newQuantity = cartItem.quantity - 1;
              handleQuantityChange(product._id, newQuantity, e);
            }}
            disabled={cartItem.quantity <= 0}
          >
            <span className="text-sm md:text-lg font-medium">−</span>
          </button>
          <span className="w-6 text-center font-medium text-gray-700 text-sm md:text-base md:w-8">
            {cartItem.quantity}
          </span>
          <button
            className={`w-6 h-6 md:w-8 md:h-8 flex items-center justify-center rounded-full bg-gradient-to-b from-blue-100 to-blue-200 hover:from-blue-200 hover:to-blue-300 text-blue-700 hover:text-blue-900 transition-all duration-200 shadow-sm hover:shadow-md ${
              !product.isInStock ? "opacity-50 cursor-not-allowed" : ""
            }`}
            onClick={(e) =>
              handleQuantityChange(product._id, cartItem.quantity + 1, e)
            }
            disabled={!product.isInStock}
          >
            <span className="text-sm md:text-lg font-medium">+</span>
          </button>
        </div>
      );
    } else {
      return (
        <button
          className={`bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white px-3 py-1.5 md:px-4 md:py-2 rounded-full text-xs md:text-sm font-medium transition-all duration-300 transform hover:scale-105 shadow-md flex items-center ${
            !product.isInStock ? "opacity-50 cursor-not-allowed" : ""
          }`}
          onClick={(e) => handleAddToCart(product, e)}
          disabled={!product.isInStock}
        >
          <AiOutlineShoppingCart className="mr-1 md:mr-2 text-sm md:text-base" />
          Add to Cart
        </button>
      );
    }
  };

  const renderRatingStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    for (let i = 0; i < fullStars; i++) {
      stars.push(<BsStarFill key={`full-${i}`} className="text-yellow-400 text-xs md:text-sm" />);
    }

    if (hasHalfStar) {
      stars.push(<BsStarHalf key="half" className="text-yellow-400 text-xs md:text-sm" />);
    }

    const emptyStars = 5 - stars.length;
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<BsStar key={`empty-${i}`} className="text-yellow-400 text-xs md:text-sm" />);
    }

    return stars;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64 md:h-96">
        <PulseLoader color="#3B82F6" size={15} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-500 text-center p-4 md:p-10 bg-red-50 rounded-xl max-w-3xl mx-auto my-8 md:my-12">
        <h3 className="text-lg md:text-xl font-bold mb-2">Error Loading Products</h3>
        <p className="text-sm md:text-base">{error}</p>
        <p className="mt-2 md:mt-4 text-sm md:text-base">Please try again later</p>
      </div>
    );
  }

  if (topRatedProducts.length === 0) {
    return (
      <div className="text-gray-600 text-center p-4 md:p-10 bg-gray-50 rounded-xl max-w-3xl mx-auto my-8 md:my-12">
        <h3 className="text-xl md:text-2xl font-bold mb-4">No Top Rated Products Found</h3>
        <p className="text-sm md:text-base">
          We couldn't find any products with active variants and a 4+ star rating at the moment.
        </p>
      </div>
    );
  }

  return (
    <section className="py-8 md:py-16 bg-gradient-to-br from-blue-50 to-indigo-50">
      {/* Login Confirmation Modal */}
      {showLoginModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-4 md:p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Login Required
            </h3>
            <p className="text-gray-600 mb-6 text-sm md:text-base">
              You need to login to add items to your cart. Would you like to
              login now?
            </p>
            <div className="flex justify-end space-x-3 md:space-x-4">
              <button
                onClick={() => setShowLoginModal(false)}
                className="px-3 py-2 md:px-4 md:py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 text-sm md:text-base"
              >
                Cancel
              </button>
              <button
                onClick={confirmAddToCart}
                className="px-3 py-2 md:px-4 md:py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm md:text-base"
              >
                Login
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="container mx-auto px-3 md:px-4">
        <div className="text-center mb-8 md:mb-12">
          <div className="inline-block bg-gradient-to-r from-blue-600 to-purple-600 text-white px-3 py-1 md:px-4 md:py-1 rounded-full mb-3">
            <span className="text-xs md:text-sm font-semibold">TOP RATED</span>
          </div>
          <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3">
            Customer <span className="text-blue-600">Favorites</span>
          </h2>
          <p className="text-sm md:text-lg text-gray-600 max-w-2xl mx-auto">
            Discover our highest rated products loved by thousands of customers
          </p>
        </div>

        <style jsx>{`
          .custom-slick-arrow:before {
            display: none;
          }
          .slick-prev:hover,
          .slick-prev:focus,
          .slick-next:hover,
          .slick-next:focus {
            background: transparent;
          }
          .slick-prev,
          .slick-next {
            width: 30px;
            height: 30px;
          }
          @media (min-width: 768px) {
            .slick-prev,
            .slick-next {
              width: 40px;
              height: 40px;
            }
          }
          .slick-prev:hover,
          .slick-next:hover {
            opacity: 1;
          }
          .slick-slide {
            padding: 0 8px;
          }
          @media (max-width: 480px) {
            .slick-slide {
              padding: 0 4px;
            }
          }
        `}</style>

        <div className="px-1 md:px-4 relative">
          <Slider {...sliderSettings}>
            {topRatedProducts.map((item, index) => {
              const product = item.product;
              const firstVariant = product.variants?.[0];
              const firstVariantId = firstVariant?._id;
              const productImage = getProductImage(product);

              return (
                <div key={product._id} className="px-1 md:px-2">
                  <div
                    className="bg-white rounded-xl md:rounded-2xl shadow-lg md:shadow-xl overflow-hidden hover:shadow-xl md:hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 md:hover:-translate-y-2 relative h-full flex flex-col"
                    onClick={() => fetchProductDetails(product._id)}
                  >
                    {/* Premium badge for top products */}
                    {index < 2 && (
                      <div className="absolute top-2 left-2 md:top-4 md:left-4 z-10">
                        <div className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-white px-2 py-0.5 md:px-3 md:py-1 rounded-full text-xs font-bold uppercase tracking-wide shadow-md">
                          {index === 0 ? "Best Seller" : "Popular"}
                        </div>
                      </div>
                    )}

                    {/* Wishlist button */}
                    {token && (
                      <button
                        onClick={(e) =>
                          toggleWishlist(product._id, firstVariantId, e)
                        }
                        disabled={loadingWishlistItems[product._id]}
                        className="absolute top-2 right-2 md:top-4 md:right-4 z-10 p-1.5 md:p-2 rounded-full bg-white bg-opacity-80 hover:bg-opacity-100 transition-all duration-200 shadow-sm"
                      >
                        {loadingWishlistItems[product._id] ? (
                          <div className="h-4 w-4 md:h-5 md:w-5 flex items-center justify-center">
                            <PulseLoader size={4} color="#EF4444" />
                          </div>
                        ) : wishlist.some(
                            (item) =>
                              item?.productId?._id === product?._id &&
                              item?.variantId === firstVariantId
                          ) ? (
                          <AiFillHeart className="h-4 w-4 md:h-5 md:w-5 text-red-500 hover:text-red-700" />
                        ) : (
                          <AiOutlineHeart className="h-4 w-4 md:h-5 md:w-5 text-gray-500 hover:text-gray-700" />
                        )}
                      </button>
                    )}

                    {/* Product Image */}
                    <div className="relative h-40 md:h-64 w-full overflow-hidden cursor-pointer bg-gray-100 flex items-center justify-center">
                      <img
                        src={productImage}
                        alt={product.name}
                        className="max-h-full max-w-full object-contain p-2 md:p-4"
                        onError={() => handleImageError(product._id)}
                        loading="lazy"
                      />
                      {!product.isInStock && (
                        <div className="absolute inset-0 bg-white bg-opacity-60 flex items-center justify-center">
                          <span className="bg-gray-800 text-white px-2 py-1 rounded-md text-xs md:text-sm font-medium">
                            Out of Stock
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="p-3 md:p-6 flex-grow flex flex-col">
                      <div className="flex justify-between items-start mb-2 md:mb-3">
                        <div className="flex-1 mr-2">
                          <h3 className="text-sm md:text-xl font-bold text-gray-900 mb-1 line-clamp-1">
                            {product.name}
                          </h3>
                          <div className="flex items-center">
                            <div className="flex">
                              {renderRatingStars(item.averageRating)}
                            </div>
                            <span className="ml-1 md:ml-2 text-xs md:text-sm text-gray-600">
                              ({item.ratingCount})
                            </span>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className="text-base md:text-2xl font-bold text-gray-900">
                            ₹{firstVariant?.price?.toLocaleString()}
                          </span>
                          {firstVariant?.mrp > firstVariant?.price && (
                            <div className="text-xs text-gray-500 line-through">
                              ₹{firstVariant?.mrp?.toLocaleString()}
                            </div>
                          )}
                        </div>
                      </div>

                      <p className="text-gray-600 mb-3 md:mb-5 line-clamp-2 flex-grow text-xs md:text-sm">
                        {product.description}
                      </p>

                      <div className="flex justify-between items-center mt-auto">
                        {!product.isInStock ? (
                          <div className="text-xs md:text-sm font-medium text-red-600 bg-red-50 px-2 py-1 md:px-3 md:py-1 rounded-full">
                            Out of Stock
                          </div>
                        ) : firstVariant?.stock <
                          (firstVariant?.stock_threshold || 5) ? (
                          <div className="text-xs md:text-sm font-medium text-orange-600 bg-orange-50 px-2 py-1 md:px-3 md:py-1 rounded-full">
                            Only {firstVariant?.stock} left!
                          </div>
                        ) : (
                          <div className="text-xs md:text-sm font-medium text-green-600 bg-green-50 px-2 py-1 md:px-3 md:py-1 rounded-full">
                            In Stock
                          </div>
                        )}

                        {renderProductActions(product)}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </Slider>
        </div>
      </div>
    </section>
  );
};

export default TopRatedProducts;

// import React, { useEffect, useState } from "react";
// import axios from "axios";
// import Cookies from "js-cookie";
// import { useNavigate } from "react-router-dom";
// import { useDispatch, useSelector } from "react-redux";
// import {
//   updateItemQuantity,
//   addToCart,
//   fetchCart,
// } from "../../../redux/thunks/cartThunks";
// import {
//   fetchWishlist,
//   addItemToWishlist,
//   removeItemFromWishlist,
// } from "../../../redux/thunks/wishlistThunks";
// import Slider from "react-slick";
// import "slick-carousel/slick/slick.css";
// import "slick-carousel/slick/slick-theme.css";
// import {
//   AiOutlineHeart,
//   AiFillHeart,
//   AiOutlineShoppingCart,
//   AiOutlineLeft,
//   AiOutlineRight,
// } from "react-icons/ai";
// import { BsStarFill, BsStarHalf, BsStar, BsStars } from "react-icons/bs";
// import { toast } from "react-toastify";
// import "react-toastify/dist/ReactToastify.css";
// import { PulseLoader } from "react-spinners";
// import fallbackImage from "../../../assets/brand-default.jpg";

// const TopRatedProducts = () => {
//   const [products, setProducts] = useState([]);
//   const [topRatedProducts, setTopRatedProducts] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [loadingWishlistItems, setLoadingWishlistItems] = useState({});
//   const [showLoginModal, setShowLoginModal] = useState(false);
//   const [productToAdd, setProductToAdd] = useState(null);
//   const [imageErrors, setImageErrors] = useState({});
//   const [windowWidth, setWindowWidth] = useState(window.innerWidth);

//   const navigate = useNavigate();
//   const dispatch = useDispatch();
//   const token = Cookies.get("ecom_user_token");
//   const cartItems = useSelector((state) => state.cart.items);
//   const { items: wishlist, loading: wishlistLoading } = useSelector(
//     (state) => state.wishlist
//   );

//   // Track window width for responsive behavior
//   useEffect(() => {
//     const handleResize = () => {
//       setWindowWidth(window.innerWidth);
//     };

//     window.addEventListener('resize', handleResize);
//     return () => window.removeEventListener('resize', handleResize);
//   }, []);

//   // Custom arrow components with responsive sizing
//   const NextArrow = (props) => {
//     const { className, style, onClick } = props;
//     const isSmallScreen = windowWidth < 768;
//     const arrowSize = isSmallScreen ? "30px" : "40px";
    
//     return (
//       <div
//         className={`${className} custom-slick-arrow`}
//         style={{
//           ...style,
//           display: "block",
//           right: isSmallScreen ? "-15px" : "-25px",
//           width: arrowSize,
//           height: arrowSize,
//           zIndex: 1,
//         }}
//         onClick={onClick}
//       >
//         <div className="w-full h-full bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-blue-50 transition-colors duration-200 border border-gray-200">
//           <AiOutlineRight className="text-gray-700 text-lg md:text-xl" />
//         </div>
//       </div>
//     );
//   };

//   const PrevArrow = (props) => {
//     const { className, style, onClick } = props;
//     const isSmallScreen = windowWidth < 768;
//     const arrowSize = isSmallScreen ? "30px" : "40px";
    
//     return (
//       <div
//         className={`${className} custom-slick-arrow`}
//         style={{
//           ...style,
//           display: "block",
//           left: isSmallScreen ? "-15px" : "-25px",
//           width: arrowSize,
//           height: arrowSize,
//           zIndex: 1,
//         }}
//         onClick={onClick}
//       >
//         <div className="w-full h-full bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-blue-50 transition-colors duration-200 border border-gray-200">
//           <AiOutlineLeft className="text-gray-700 text-lg md:text-xl" />
//         </div>
//       </div>
//     );
//   };

//   // Responsive slider settings
//   const sliderSettings = {
//     dots: windowWidth > 768, // Hide dots on small screens
//     infinite: true,
//     speed: 500,
//     slidesToShow: 4,
//     slidesToScroll: 1,
//     nextArrow: <NextArrow />,
//     prevArrow: <PrevArrow />,
//     responsive: [
//       {
//         breakpoint: 1280, // Large screens
//         settings: {
//           slidesToShow: 4,
//           slidesToScroll: 1,
//         }
//       },
//       {
//         breakpoint: 1024, // Medium screens
//         settings: {
//           slidesToShow: 3,
//           slidesToScroll: 1,
//           dots: true,
//         },
//       },
//       {
//         breakpoint: 768, // Small tablets
//         settings: {
//           slidesToShow: 2,
//           slidesToScroll: 1,
//           dots: false,
//         },
//       },
//       {
//         breakpoint: 480, // Mobile devices
//         settings: {
//           slidesToShow: 1,
//           slidesToScroll: 1,
//           dots: false,
//           arrows: windowWidth > 350, // Hide arrows on very small screens
//         },
//       },
//     ],
//   };

//   useEffect(() => {
//     if (token) {
//       dispatch(fetchWishlist());
//     }
//   }, [token, dispatch]);

//   useEffect(() => {
//     dispatch(fetchCart());
//   }, [dispatch]);

//   const handleImageError = (productId) => {
//     setImageErrors((prev) => ({
//       ...prev,
//       [productId]: true,
//     }));
//   };

//   const getProductImage = (product) => {
//     if (imageErrors[product._id]) {
//       return fallbackImage;
//     }
//     return (
//       product.variants?.[0]?.images?.[0] || product.images?.[0] || fallbackImage
//     );
//   };

//   useEffect(() => {
//     const fetchTopRatedProducts = async () => {
//       try {
//         const response = await axios.get(
//           `${process.env.REACT_APP_BACKEND_URL}/api/seller/product/get-by-rating`
//         );

//         // Process products to find active variants only
//         const processedProducts = response?.data?.products
//           ?.map((item) => {
//             const product = item.product;

//             // Filter out non-active variants
//             const activeVariants = product.variants?.filter(
//               (v) => v.status === "active"
//             );

//             if (activeVariants.length === 0) return null; // Skip products with no active variants

//             // Create a new product object with only active variants
//             return {
//               ...item,
//               product: {
//                 ...product,
//                 variants: activeVariants,
//                 // Add a flag to indicate if the product is in stock
//                 isInStock: activeVariants.some(
//                   (v) => v.stock > 0 || v.stock === undefined
//                 ),
//               },
//             };
//           })
//           .filter(Boolean); // Remove null entries

//         // Sort products by rating (highest first) and then by number of ratings
//         const sortedProducts = processedProducts.sort((a, b) => {
//           if (b.averageRating !== a.averageRating) {
//             return b.averageRating - a.averageRating;
//           }
//           return b.ratingCount - a.ratingCount;
//         });

//         setProducts(sortedProducts);
//         // Take top 5 products for the slider
//         setTopRatedProducts(sortedProducts.slice(0, 5));
//         setLoading(false);
//       } catch (err) {
//         setError(err.message);
//         setLoading(false);
//         console.error("Error fetching top rated products:", err);
//       }
//     };

//     fetchTopRatedProducts();
//   }, []);

//   const fetchProductDetails = (productId) => {
//     navigate(`/product/${productId}`);
//   };

//   const handleAddToCart = async (product, e) => {
//     e.stopPropagation();

//     if (!token) {
//       setProductToAdd(product);
//       setShowLoginModal(true);
//       return;
//     }

//     const firstVariantId = product.variants?.[0]?._id;

//     if (!product.isInStock) {
//       toast.warning("This item is out of stock");
//       return;
//     }

//     try {
//       await dispatch(
//         addToCart({
//           productId: product._id,
//           variantId: product.variants?.length > 0 ? firstVariantId : undefined,
//           quantity: 1,
//         })
//       );
//       toast.success("Added to cart!");
//     } catch (error) {
//       console.error("Add to cart failed:", error);
//       toast.error(error.response?.data?.message || "Failed to add to cart");
//     }
//   };

//   const confirmAddToCart = async () => {
//     setShowLoginModal(false);
//     navigate("/login", { state: { from: window.location.pathname } });
//   };

//   const handleQuantityChange = async (productId, newQuantity, e) => {
//     e.stopPropagation();
//     const product = products.find((p) => p.product._id === productId)?.product;
//     const firstVariantId = product?.variants?.[0]?._id;

//     try {
//       await dispatch(
//         updateItemQuantity({
//           productId,
//           variantId: product?.variants?.length > 0 ? firstVariantId : undefined,
//           quantity: newQuantity,
//         })
//       );
//     } catch (error) {
//       console.error("Error updating quantity:", error);
//       toast.error("Failed to update quantity. Please try again.");
//     }
//   };

//   const toggleWishlist = async (productId, variantId, e) => {
//     e.stopPropagation();
//     if (!token) {
//       toast.info("Please login to manage your wishlist");
//       navigate("/login");
//       return;
//     }

//     try {
//       setLoadingWishlistItems((prev) => ({ ...prev, [productId]: true }));

//       const isInWishlist = wishlist.some(
//         (item) =>
//           item.productId._id === productId && item.variantId === variantId
//       );

//       if (isInWishlist) {
//         await dispatch(removeItemFromWishlist(productId, variantId));
//         toast.success("Removed from wishlist");
//       } else {
//         await dispatch(addItemToWishlist(productId, variantId));
//         toast.success("Added to wishlist");
//       }
//     } catch (error) {
//       console.error("Wishlist operation failed:", error);
//       toast.error(
//         error.response?.data?.message ||
//           "Failed to update wishlist. Please try again."
//       );
//     } finally {
//       setLoadingWishlistItems((prev) => ({ ...prev, [productId]: false }));
//     }
//   };

//   const renderProductActions = (product) => {
//     const firstVariant = product.variants?.[0];
//     const firstVariantId = firstVariant?._id;

//     if (!product.isInStock) {
//       return (
//         <button
//           className="px-3 py-1.5 md:px-4 md:py-2 rounded-full bg-gray-300 text-gray-600 cursor-not-allowed text-xs md:text-sm font-medium"
//           disabled
//         >
//           Out of Stock
//         </button>
//       );
//     }

//     const cartItem = cartItems.find(
//       (item) =>
//         item.productId === product._id &&
//         (product.variants?.length > 0
//           ? item.variantId === firstVariantId
//           : true)
//     );

//     if (cartItem) {
//       return (
//         <div className="flex items-center justify-center space-x-1 md:space-x-2">
//           <button
//             className={`w-6 h-6 md:w-8 md:h-8 flex items-center justify-center rounded-full transition-all duration-200 shadow-sm ${
//               cartItem.quantity <= 0
//                 ? "bg-gray-100 text-gray-400 cursor-not-allowed"
//                 : "bg-gradient-to-b from-gray-100 to-gray-200 hover:from-gray-200 hover:to-gray-300 text-gray-700 hover:text-gray-900 hover:shadow-md"
//             }`}
//             onClick={(e) => {
//               const newQuantity = cartItem.quantity - 1;
//               handleQuantityChange(product._id, newQuantity, e);
//             }}
//             disabled={cartItem.quantity <= 0}
//           >
//             <span className="text-sm md:text-lg font-medium">−</span>
//           </button>
//           <span className="w-6 md:w-8 text-center font-medium text-gray-700 text-sm md:text-base">
//             {cartItem.quantity}
//           </span>
//           <button
//             className={`w-6 h-6 md:w-8 md:h-8 flex items-center justify-center rounded-full bg-gradient-to-b from-blue-100 to-blue-200 hover:from-blue-200 hover:to-blue-300 text-blue-700 hover:text-blue-900 transition-all duration-200 shadow-sm hover:shadow-md ${
//               !product.isInStock ? "opacity-50 cursor-not-allowed" : ""
//             }`}
//             onClick={(e) =>
//               handleQuantityChange(product._id, cartItem.quantity + 1, e)
//             }
//             disabled={!product.isInStock}
//           >
//             <span className="text-sm md:text-lg font-medium">+</span>
//           </button>
//         </div>
//       );
//     } else {
//       return (
//         <button
//           className={`bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white px-3 py-1.5 md:px-4 md:py-2 rounded-full text-xs md:text-sm font-medium transition-all duration-300 transform hover:scale-105 shadow-md flex items-center ${
//             !product.isInStock ? "opacity-50 cursor-not-allowed" : ""
//           }`}
//           onClick={(e) => handleAddToCart(product, e)}
//           disabled={!product.isInStock}
//         >
//           <AiOutlineShoppingCart className="mr-1 md:mr-2 text-sm md:text-base" />
//           Add to Cart
//         </button>
//       );
//     }
//   };

//   const renderRatingStars = (rating) => {
//     const stars = [];
//     const fullStars = Math.floor(rating);
//     const hasHalfStar = rating % 1 >= 0.5;

//     for (let i = 0; i < fullStars; i++) {
//       stars.push(<BsStarFill key={`full-${i}`} className="text-yellow-400 text-xs md:text-sm" />);
//     }

//     if (hasHalfStar) {
//       stars.push(<BsStarHalf key="half" className="text-yellow-400 text-xs md:text-sm" />);
//     }

//     const emptyStars = 5 - stars.length;
//     for (let i = 0; i < emptyStars; i++) {
//       stars.push(<BsStar key={`empty-${i}`} className="text-yellow-400 text-xs md:text-sm" />);
//     }

//     return stars;
//   };

//   if (loading) {
//     return (
//       <div className="flex justify-center items-center h-64 md:h-96">
//         <PulseLoader color="#3B82F6" size={15} />
//       </div>
//     );
//   }

//   if (error) {
//     return (
//       <div className="text-red-500 text-center p-6 md:p-10 bg-red-50 rounded-xl max-w-3xl mx-auto my-8 md:my-12">
//         <h3 className="text-lg md:text-xl font-bold mb-2">Error Loading Products</h3>
//         <p className="text-sm md:text-base">{error}</p>
//         <p className="mt-2 md:mt-4 text-sm md:text-base">Please try again later</p>
//       </div>
//     );
//   }

//   if (topRatedProducts.length === 0) {
//     return (
//       <div className="text-gray-600 text-center p-6 md:p-10 bg-gray-50 rounded-xl max-w-3xl mx-auto my-8 md:my-12">
//         <h3 className="text-xl md:text-2xl font-bold mb-4">No Top Rated Products Found</h3>
//         <p className="text-sm md:text-base">
//           We couldn't find any products with active variants and a 4+ star rating at the moment.
//         </p>
//       </div>
//     );
//   }

//   return (
//     <section className="py-8 md:py-16 bg-gradient-to-br from-blue-50 to-indigo-50">
//       {/* Login Confirmation Modal */}
//       {showLoginModal && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//           <div className="bg-white rounded-lg p-4 md:p-6 max-w-md w-full">
//             <h3 className="text-lg font-medium text-gray-900 mb-4">
//               Login Required
//             </h3>
//             <p className="text-gray-600 mb-6 text-sm md:text-base">
//               You need to login to add items to your cart. Would you like to
//               login now?
//             </p>
//             <div className="flex justify-end space-x-3 md:space-x-4">
//               <button
//                 onClick={() => setShowLoginModal(false)}
//                 className="px-3 py-2 md:px-4 md:py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 text-sm md:text-base"
//               >
//                 Cancel
//               </button>
//               <button
//                 onClick={confirmAddToCart}
//                 className="px-3 py-2 md:px-4 md:py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm md:text-base"
//               >
//                 Login
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       <div className="container mx-auto px-3 md:px-4">
//         <div className="text-center mb-8 md:mb-12">
//           <div className="inline-block bg-gradient-to-r from-blue-600 to-purple-600 text-white px-3 py-1 md:px-4 md:py-1 rounded-full mb-3">
//             <span className="text-xs md:text-sm font-semibold">TOP RATED</span>
//           </div>
//           <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3">
//             Customer <span className="text-blue-600">Favorites</span>
//           </h2>
//           <p className="text-sm md:text-lg text-gray-600 max-w-2xl mx-auto">
//             Discover our highest rated products loved by thousands of customers
//           </p>
//         </div>

//         <style jsx>{`
//           .custom-slick-arrow:before {
//             display: none;
//           }
//           .slick-prev:hover,
//           .slick-prev:focus,
//           .slick-next:hover,
//           .slick-next:focus {
//             background: transparent;
//           }
//           .slick-prev,
//           .slick-next {
//             width: 30px;
//             height: 30px;
//           }
//           @media (min-width: 768px) {
//             .slick-prev,
//             .slick-next {
//               width: 40px;
//               height: 40px;
//             }
//           }
//           .slick-prev:hover,
//           .slick-next:hover {
//             opacity: 1;
//           }
//         `}</style>

//         <div className="px-2 md:px-4 relative">
//           <Slider {...sliderSettings}>
//             {topRatedProducts.map((item, index) => {
//               const product = item.product;
//               const firstVariant = product.variants?.[0];
//               const firstVariantId = firstVariant?._id;
//               const productImage = getProductImage(product);

//               return (
//                 <div key={product._id} className="px-1 md:px-2">
//                   <div
//                     className="bg-white rounded-xl md:rounded-2xl shadow-lg md:shadow-xl overflow-hidden hover:shadow-xl md:hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 md:hover:-translate-y-2 relative h-full flex flex-col"
//                     onClick={() => fetchProductDetails(product._id)}
//                   >
//                     {/* Premium badge for top products */}
//                     {index < 2 && (
//                       <div className="absolute top-2 left-2 md:top-4 md:left-4 z-10">
//                         <div className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-white px-2 py-0.5 md:px-3 md:py-1 rounded-full text-xs font-bold uppercase tracking-wide shadow-md">
//                           {index === 0 ? "Best Seller" : "Popular"}
//                         </div>
//                       </div>
//                     )}

//                     {/* Wishlist button */}
//                     {token && (
//                       <button
//                         onClick={(e) =>
//                           toggleWishlist(product._id, firstVariantId, e)
//                         }
//                         disabled={loadingWishlistItems[product._id]}
//                         className="absolute top-2 right-2 md:top-4 md:right-4 z-10 p-1.5 md:p-2 rounded-full bg-white bg-opacity-80 hover:bg-opacity-100 transition-all duration-200 shadow-sm"
//                       >
//                         {loadingWishlistItems[product._id] ? (
//                           <div className="h-4 w-4 md:h-5 md:w-5 flex items-center justify-center">
//                             <PulseLoader size={4} color="#EF4444" />
//                           </div>
//                         ) : wishlist.some(
//                             (item) =>
//                               item?.productId?._id === product?._id &&
//                               item?.variantId === firstVariantId
//                           ) ? (
//                           <AiFillHeart className="h-4 w-4 md:h-5 md:w-5 text-red-500 hover:text-red-700" />
//                         ) : (
//                           <AiOutlineHeart className="h-4 w-4 md:h-5 md:w-5 text-gray-500 hover:text-gray-700" />
//                         )}
//                       </button>
//                     )}

//                     {/* Product Image */}
//                     <div className="relative h-48 md:h-64 w-full overflow-hidden cursor-pointer bg-gray-100 flex items-center justify-center">
//                       <img
//                         src={productImage}
//                         alt={product.name}
//                         className="max-h-full max-w-full object-contain p-2 md:p-4"
//                         onError={() => handleImageError(product._id)}
//                         loading="lazy"
//                       />
//                       {!product.isInStock && (
//                         <div className="absolute inset-0 bg-white bg-opacity-60 flex items-center justify-center">
//                           <span className="bg-gray-800 text-white px-2 py-1 rounded-md text-xs md:text-sm font-medium">
//                             Out of Stock
//                           </span>
//                         </div>
//                       )}
//                     </div>

//                     <div className="p-4 md:p-6 flex-grow flex flex-col">
//                       <div className="flex justify-between items-start mb-3">
//                         <div className="flex-1 mr-2">
//                           <h3 className="text-base md:text-xl font-bold text-gray-900 mb-1 line-clamp-1">
//                             {product.name}
//                           </h3>
//                           <div className="flex items-center">
//                             <div className="flex">
//                               {renderRatingStars(item.averageRating)}
//                             </div>
//                             <span className="ml-1 md:ml-2 text-xs md:text-sm text-gray-600">
//                               ({item.ratingCount} review
//                               {item.ratingCount !== 1 ? "s" : ""})
//                             </span>
//                           </div>
//                         </div>
//                         <div className="text-right">
//                           <span className="text-lg md:text-2xl font-bold text-gray-900">
//                             ₹{firstVariant?.price?.toLocaleString()}
//                           </span>
//                           {firstVariant?.mrp > firstVariant?.price && (
//                             <div className="text-xs text-gray-500 line-through">
//                               ₹{firstVariant?.mrp?.toLocaleString()}
//                             </div>
//                           )}
//                         </div>
//                       </div>

//                       <p className="text-gray-600 mb-4 md:mb-5 line-clamp-2 flex-grow text-xs md:text-sm">
//                         {product.description}
//                       </p>

//                       <div className="flex justify-between items-center mt-auto">
//                         {!product.isInStock ? (
//                           <div className="text-xs md:text-sm font-medium text-red-600 bg-red-50 px-2 py-1 md:px-3 md:py-1 rounded-full">
//                             Out of Stock
//                           </div>
//                         ) : firstVariant?.stock <
//                           (firstVariant?.stock_threshold || 5) ? (
//                           <div className="text-xs md:text-sm font-medium text-orange-600 bg-orange-50 px-2 py-1 md:px-3 md:py-1 rounded-full">
//                             Only {firstVariant?.stock} left!
//                           </div>
//                         ) : (
//                           <div className="text-xs md:text-sm font-medium text-green-600 bg-green-50 px-2 py-1 md:px-3 md:py-1 rounded-full">
//                             In Stock
//                           </div>
//                         )}

//                         {renderProductActions(product)}
//                       </div>
//                     </div>
//                   </div>
//                 </div>
//               );
//             })}
//           </Slider>
//         </div>
//       </div>
//     </section>
//   );
// };

// export default TopRatedProducts;

// import React, { useEffect, useState } from "react";
// import axios from "axios";
// import Cookies from "js-cookie";
// import { useNavigate } from "react-router-dom";
// import { useDispatch, useSelector } from "react-redux";
// import {
//   updateItemQuantity,
//   addToCart,
//   fetchCart,
// } from "../../../redux/thunks/cartThunks";
// import {
//   fetchWishlist,
//   addItemToWishlist,
//   removeItemFromWishlist,
// } from "../../../redux/thunks/wishlistThunks";
// import Slider from "react-slick";
// import "slick-carousel/slick/slick.css";
// import "slick-carousel/slick/slick-theme.css";
// import {
//   AiOutlineHeart,
//   AiFillHeart,
//   AiOutlineShoppingCart,
//   AiOutlineLeft,
//   AiOutlineRight,
// } from "react-icons/ai";
// import { BsStarFill, BsStarHalf, BsStar, BsStars } from "react-icons/bs";
// import { toast } from "react-toastify";
// import "react-toastify/dist/ReactToastify.css";
// import { PulseLoader } from "react-spinners";
// import fallbackImage from "../../../assets/brand-default.jpg";

// const TopRatedProducts = () => {
//   const [products, setProducts] = useState([]);
//   const [topRatedProducts, setTopRatedProducts] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [loadingWishlistItems, setLoadingWishlistItems] = useState({});
//   const [showLoginModal, setShowLoginModal] = useState(false);
//   const [productToAdd, setProductToAdd] = useState(null);
//   const [imageErrors, setImageErrors] = useState({});

//   const navigate = useNavigate();
//   const dispatch = useDispatch();
//   const token = Cookies.get("ecom_user_token");
//   const cartItems = useSelector((state) => state.cart.items);
//   const { items: wishlist, loading: wishlistLoading } = useSelector(
//     (state) => state.wishlist
//   );

//   // Custom arrow components
//   const NextArrow = (props) => {
//     const { className, style, onClick } = props;
//     return (
//       <div
//         className={`${className} custom-slick-arrow`}
//         style={{
//           ...style,
//           display: "block",
//           right: "-25px",
//           width: "40px",
//           height: "40px",
//           zIndex: 1,
//         }}
//         onClick={onClick}
//       >
//         <div className="w-full h-full bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-blue-50 transition-colors duration-200 border border-gray-200">
//           <AiOutlineRight className="text-gray-700 text-xl" />
//         </div>
//       </div>
//     );
//   };

//   const PrevArrow = (props) => {
//     const { className, style, onClick } = props;
//     return (
//       <div
//         className={`${className} custom-slick-arrow`}
//         style={{
//           ...style,
//           display: "block",
//           left: "-25px",
//           width: "40px",
//           height: "40px",
//           zIndex: 1,
//         }}
//         onClick={onClick}
//       >
//         <div className="w-full h-full bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-blue-50 transition-colors duration-200 border border-gray-200">
//           <AiOutlineLeft className="text-gray-700 text-xl" />
//         </div>
//       </div>
//     );
//   };

//   // Slider settings
//   const sliderSettings = {
//     dots: true,
//     infinite: true,
//     speed: 500,
//     slidesToShow: 4,
//     slidesToScroll: 1,
//     nextArrow: <NextArrow />,
//     prevArrow: <PrevArrow />,
//     responsive: [
//       {
//         breakpoint: 1024,
//         settings: {
//           slidesToShow: 3,
//           slidesToScroll: 1,
//           infinite: true,
//           dots: true,
//         },
//       },
//       {
//         breakpoint: 768,
//         settings: {
//           slidesToShow: 2,
//           slidesToScroll: 1,
//           initialSlide: 2,
//         },
//       },
//       {
//         breakpoint: 480,
//         settings: {
//           slidesToShow: 1,
//           slidesToScroll: 1,
//         },
//       },
//     ],
//   };

//   useEffect(() => {
//     if (token) {
//       dispatch(fetchWishlist());
//     }
//   }, [token, dispatch]);

//   useEffect(() => {
//     dispatch(fetchCart());
//   }, [dispatch]);

//   const handleImageError = (productId) => {
//     setImageErrors((prev) => ({
//       ...prev,
//       [productId]: true,
//     }));
//   };

//   const getProductImage = (product) => {
//     if (imageErrors[product._id]) {
//       return fallbackImage;
//     }
//     return (
//       product.variants?.[0]?.images?.[0] || product.images?.[0] || fallbackImage
//     );
//   };

//   useEffect(() => {
//     const fetchTopRatedProducts = async () => {
//       try {
//         const response = await axios.get(
//           `${process.env.REACT_APP_BACKEND_URL}/api/seller/product/get-by-rating`
//         );

//         // Process products to find active variants only
//         const processedProducts = response?.data?.products
//           ?.map((item) => {
//             const product = item.product;

//             // Filter out non-active variants
//             const activeVariants = product.variants?.filter(
//               (v) => v.status === "active"
//             );

//             if (activeVariants.length === 0) return null; // Skip products with no active variants

//             // Create a new product object with only active variants
//             return {
//               ...item,
//               product: {
//                 ...product,
//                 variants: activeVariants,
//                 // Add a flag to indicate if the product is in stock
//                 isInStock: activeVariants.some(
//                   (v) => v.stock > 0 || v.stock === undefined
//                 ),
//               },
//             };
//           })
//           .filter(Boolean); // Remove null entries

//         // Sort products by rating (highest first) and then by number of ratings
//         const sortedProducts = processedProducts.sort((a, b) => {
//           if (b.averageRating !== a.averageRating) {
//             return b.averageRating - a.averageRating;
//           }
//           return b.ratingCount - a.ratingCount;
//         });

//         setProducts(sortedProducts);
//         // Take top 5 products for the slider
//         setTopRatedProducts(sortedProducts.slice(0, 5));
//         setLoading(false);
//       } catch (err) {
//         setError(err.message);
//         setLoading(false);
//         console.error("Error fetching top rated products:", err);
//       }
//     };

//     fetchTopRatedProducts();
//   }, []);

//   const fetchProductDetails = (productId) => {
//     navigate(`/product/${productId}`);
//   };

//   const handleAddToCart = async (product, e) => {
//     e.stopPropagation();

//     if (!token) {
//       setProductToAdd(product);
//       setShowLoginModal(true);
//       return;
//     }

//     const firstVariantId = product.variants?.[0]?._id;

//     if (!product.isInStock) {
//       toast.warning("This item is out of stock");
//       return;
//     }

//     try {
//       await dispatch(
//         addToCart({
//           productId: product._id,
//           variantId: product.variants?.length > 0 ? firstVariantId : undefined,
//           quantity: 1,
//         })
//       );
//       toast.success("Added to cart!");
//     } catch (error) {
//       console.error("Add to cart failed:", error);
//       toast.error(error.response?.data?.message || "Failed to add to cart");
//     }
//   };

//   const confirmAddToCart = async () => {
//     setShowLoginModal(false);
//     navigate("/login", { state: { from: window.location.pathname } });
//   };

//   const handleQuantityChange = async (productId, newQuantity, e) => {
//     e.stopPropagation();
//     const product = products.find((p) => p.product._id === productId)?.product;
//     const firstVariantId = product?.variants?.[0]?._id;

//     try {
//       await dispatch(
//         updateItemQuantity({
//           productId,
//           variantId: product?.variants?.length > 0 ? firstVariantId : undefined,
//           quantity: newQuantity,
//         })
//       );
//     } catch (error) {
//       console.error("Error updating quantity:", error);
//       toast.error("Failed to update quantity. Please try again.");
//     }
//   };

//   const toggleWishlist = async (productId, variantId, e) => {
//     e.stopPropagation();
//     if (!token) {
//       toast.info("Please login to manage your wishlist");
//       navigate("/login");
//       return;
//     }

//     try {
//       setLoadingWishlistItems((prev) => ({ ...prev, [productId]: true }));

//       const isInWishlist = wishlist.some(
//         (item) =>
//           item.productId._id === productId && item.variantId === variantId
//       );

//       if (isInWishlist) {
//         await dispatch(removeItemFromWishlist(productId, variantId));
//         toast.success("Removed from wishlist");
//       } else {
//         await dispatch(addItemToWishlist(productId, variantId));
//         toast.success("Added to wishlist");
//       }
//     } catch (error) {
//       console.error("Wishlist operation failed:", error);
//       toast.error(
//         error.response?.data?.message ||
//           "Failed to update wishlist. Please try again."
//       );
//     } finally {
//       setLoadingWishlistItems((prev) => ({ ...prev, [productId]: false }));
//     }
//   };

//   const renderProductActions = (product) => {
//     const firstVariant = product.variants?.[0];
//     const firstVariantId = firstVariant?._id;

//     if (!product.isInStock) {
//       return (
//         <button
//           className="px-4 py-2 rounded-full bg-gray-300 text-gray-600 cursor-not-allowed text-sm font-medium"
//           disabled
//         >
//           Out of Stock
//         </button>
//       );
//     }

//     const cartItem = cartItems.find(
//       (item) =>
//         item.productId === product._id &&
//         (product.variants?.length > 0
//           ? item.variantId === firstVariantId
//           : true)
//     );

//     if (cartItem) {
//       return (
//         <div className="flex items-center justify-center space-x-2">
//           <button
//             className={`w-8 h-8 flex items-center justify-center rounded-full transition-all duration-200 shadow-sm ${
//               cartItem.quantity <= 0
//                 ? "bg-gray-100 text-gray-400 cursor-not-allowed"
//                 : "bg-gradient-to-b from-gray-100 to-gray-200 hover:from-gray-200 hover:to-gray-300 text-gray-700 hover:text-gray-900 hover:shadow-md"
//             }`}
//             onClick={(e) => {
//               const newQuantity = cartItem.quantity - 1;
//               handleQuantityChange(product._id, newQuantity, e);
//             }}
//             disabled={cartItem.quantity <= 0}
//           >
//             <span className="text-lg font-medium">−</span>
//           </button>
//           <span className="w-8 text-center font-medium text-gray-700">
//             {cartItem.quantity}
//           </span>
//           <button
//             className={`w-8 h-8 flex items-center justify-center rounded-full bg-gradient-to-b from-blue-100 to-blue-200 hover:from-blue-200 hover:to-blue-300 text-blue-700 hover:text-blue-900 transition-all duration-200 shadow-sm hover:shadow-md ${
//               !product.isInStock ? "opacity-50 cursor-not-allowed" : ""
//             }`}
//             onClick={(e) =>
//               handleQuantityChange(product._id, cartItem.quantity + 1, e)
//             }
//             disabled={!product.isInStock}
//           >
//             <span className="text-lg font-medium">+</span>
//           </button>
//         </div>
//       );
//     } else {
//       return (
//         <button
//           className={`bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 transform hover:scale-105 shadow-md flex items-center ${
//             !product.isInStock ? "opacity-50 cursor-not-allowed" : ""
//           }`}
//           onClick={(e) => handleAddToCart(product, e)}
//           disabled={!product.isInStock}
//         >
//           <AiOutlineShoppingCart className="mr-2" />
//           Add to Cart
//         </button>
//       );
//     }
//   };

//   const renderRatingStars = (rating) => {
//     const stars = [];
//     const fullStars = Math.floor(rating);
//     const hasHalfStar = rating % 1 >= 0.5;

//     for (let i = 0; i < fullStars; i++) {
//       stars.push(<BsStarFill key={`full-${i}`} className="text-yellow-400" />);
//     }

//     if (hasHalfStar) {
//       stars.push(<BsStarHalf key="half" className="text-yellow-400" />);
//     }

//     const emptyStars = 5 - stars.length;
//     for (let i = 0; i < emptyStars; i++) {
//       stars.push(<BsStar key={`empty-${i}`} className="text-yellow-400" />);
//     }

//     return stars;
//   };

//   if (loading) {
//     return (
//       <div className="flex justify-center items-center h-96">
//         <PulseLoader color="#3B82F6" size={15} />
//       </div>
//     );
//   }

//   if (error) {
//     return (
//       <div className="text-red-500 text-center p-10 bg-red-50 rounded-xl max-w-3xl mx-auto my-12">
//         <h3 className="text-xl font-bold mb-2">Error Loading Products</h3>
//         <p>{error}</p>
//         <p className="mt-4">Please try again later</p>
//       </div>
//     );
//   }

//   if (topRatedProducts.length === 0) {
//     return (
//       <div className="text-gray-600 text-center p-10 bg-gray-50 rounded-xl max-w-3xl mx-auto my-12">
//         <h3 className="text-2xl font-bold mb-4">No Top Rated Products Found</h3>
//         <p>
//           We couldn't find any products with active variants and a 4+ star rating at the moment.
//         </p>
//       </div>
//     );
//   }

//   return (
//     <section className="py-16 bg-gradient-to-br from-blue-50 to-indigo-50">
//       {/* Login Confirmation Modal */}
//       {showLoginModal && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//           <div className="bg-white rounded-lg p-6 max-w-md w-full">
//             <h3 className="text-lg font-medium text-gray-900 mb-4">
//               Login Required
//             </h3>
//             <p className="text-gray-600 mb-6">
//               You need to login to add items to your cart. Would you like to
//               login now?
//             </p>
//             <div className="flex justify-end space-x-4">
//               <button
//                 onClick={() => setShowLoginModal(false)}
//                 className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
//               >
//                 Cancel
//               </button>
//               <button
//                 onClick={confirmAddToCart}
//                 className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
//               >
//                 Login
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       <div className="container mx-auto px-4">
//         <div className="text-center mb-12">
//           <div className="inline-block bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-1 rounded-full mb-3">
//             <span className="text-sm font-semibold">TOP RATED</span>
//           </div>
//           <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-3">
//             Customer <span className="text-blue-600">Favorites</span>
//           </h2>
//           <p className="text-lg text-gray-600 max-w-2xl mx-auto">
//             Discover our highest rated products loved by thousands of customers
//           </p>
//         </div>

//         <style jsx>{`
//           .custom-slick-arrow:before {
//             display: none;
//           }
//           .slick-prev:hover,
//           .slick-prev:focus,
//           .slick-next:hover,
//           .slick-next:focus {
//             background: transparent;
//           }
//           .slick-prev,
//           .slick-next {
//             width: 40px;
//             height: 40px;
//           }
//           .slick-prev:hover,
//           .slick-next:hover {
//             opacity: 1;
//           }
//         `}</style>

//         <div className="px-4 relative">
//           <Slider {...sliderSettings}>
//             {topRatedProducts.map((item, index) => {
//               const product = item.product;
//               const firstVariant = product.variants?.[0];
//               const firstVariantId = firstVariant?._id;
//               const productImage = getProductImage(product);

//               return (
//                 <div key={product._id} className="px-2">
//                   <div
//                     className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 relative h-full flex flex-col"
//                     onClick={() => fetchProductDetails(product._id)}
//                   >
//                     {/* Premium badge for top products */}
//                     {index < 2 && (
//                       <div className="absolute top-4 left-4 z-10">
//                         <div className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-white px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide shadow-md">
//                           {index === 0 ? "Best Seller" : "Popular"}
//                         </div>
//                       </div>
//                     )}

//                     {/* Wishlist button */}
//                     {token && (
//                       <button
//                         onClick={(e) =>
//                           toggleWishlist(product._id, firstVariantId, e)
//                         }
//                         disabled={loadingWishlistItems[product._id]}
//                         className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white bg-opacity-80 hover:bg-opacity-100 transition-all duration-200 shadow-sm"
//                       >
//                         {loadingWishlistItems[product._id] ? (
//                           <div className="h-5 w-5 flex items-center justify-center">
//                             <PulseLoader size={6} color="#EF4444" />
//                           </div>
//                         ) : wishlist.some(
//                             (item) =>
//                               item?.productId?._id === product?._id &&
//                               item?.variantId === firstVariantId
//                           ) ? (
//                           <AiFillHeart className="h-5 w-5 text-red-500 hover:text-red-700" />
//                         ) : (
//                           <AiOutlineHeart className="h-5 w-5 text-gray-500 hover:text-gray-700" />
//                         )}
//                       </button>
//                     )}

//                     {/* Product Image */}
//                     <div className="relative h-64 w-full overflow-hidden cursor-pointer bg-gray-100 flex items-center justify-center">
//                       <img
//                         src={productImage}
//                         alt={product.name}
//                         className="max-h-full max-w-full object-contain"
//                         onError={() => handleImageError(product._id)}
//                         loading="lazy"
//                       />
//                       {!product.isInStock && (
//                         <div className="absolute inset-0 bg-white bg-opacity-60 flex items-center justify-center">
//                           <span className="bg-gray-800 text-white px-3 py-1 rounded-md text-sm font-medium">
//                             Out of Stock
//                           </span>
//                         </div>
//                       )}
//                     </div>

//                     <div className="p-6 flex-grow flex flex-col">
//                       <div className="flex justify-between items-start mb-3">
//                         <div>
//                           <h3 className="text-xl font-bold text-gray-900 mb-1 line-clamp-1">
//                             {product.name}
//                           </h3>
//                           <div className="flex items-center">
//                             <div className="flex">
//                               {renderRatingStars(item.averageRating)}
//                             </div>
//                             <span className="ml-2 text-sm text-gray-600">
//                               ({item.ratingCount} review
//                               {item.ratingCount !== 1 ? "s" : ""})
//                             </span>
//                           </div>
//                         </div>
//                         <div className="text-right">
//                           <span className="text-2xl font-bold text-gray-900">
//                             ₹{firstVariant?.price?.toLocaleString()}
//                           </span>
//                           {firstVariant?.mrp > firstVariant?.price && (
//                             <div className="text-xs text-gray-500 line-through">
//                               ₹{firstVariant?.mrp?.toLocaleString()}
//                             </div>
//                           )}
//                         </div>
//                       </div>

//                       <p className="text-gray-600 mb-5 line-clamp-2 flex-grow truncate">
//                         {product.description}
//                       </p>

//                       <div className="flex justify-between items-center mt-auto">
//                         {!product.isInStock ? (
//                           <div className="text-sm font-medium text-red-600 bg-red-50 px-3 py-1 rounded-full">
//                             Out of Stock
//                           </div>
//                         ) : firstVariant?.stock <
//                           (firstVariant?.stock_threshold || 5) ? (
//                           <div className="text-sm font-medium text-orange-600 bg-orange-50 px-3 py-1 rounded-full">
//                             Only {firstVariant?.stock} left!
//                           </div>
//                         ) : (
//                           <div className="text-sm font-medium text-green-600 bg-green-50 px-3 py-1 rounded-full">
//                             In Stock
//                           </div>
//                         )}

//                         {renderProductActions(product)}
//                       </div>
//                     </div>
//                   </div>
//                 </div>
//               );
//             })}
//           </Slider>
//         </div>
//       </div>
//     </section>
//   );
// };

// export default TopRatedProducts;

// import React, { useEffect, useState } from "react";
// import axios from "axios";
// import Cookies from "js-cookie";
// import { useNavigate } from "react-router-dom";
// import { useDispatch, useSelector } from "react-redux";
// import {
//   updateItemQuantity,
//   addToCart,
//   fetchCart,
// } from "../../../redux/thunks/cartThunks";
// import {
//   fetchWishlist,
//   addItemToWishlist,
//   removeItemFromWishlist,
// } from "../../../redux/thunks/wishlistThunks";
// import Slider from "react-slick";
// import "slick-carousel/slick/slick.css";
// import "slick-carousel/slick/slick-theme.css";
// import {
//   AiOutlineHeart,
//   AiFillHeart,
//   AiOutlineShoppingCart,
// } from "react-icons/ai";
// import { BsStarFill, BsStarHalf, BsStar, BsStars } from "react-icons/bs";
// import { toast } from "react-toastify";
// import "react-toastify/dist/ReactToastify.css";
// import { PulseLoader } from "react-spinners";
// import fallbackImage from "../../../assets/brand-default.jpg";

// const TopRatedProducts = () => {
//   const [products, setProducts] = useState([]);
//   const [topRatedProducts, setTopRatedProducts] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [loadingWishlistItems, setLoadingWishlistItems] = useState({});
//   const [showLoginModal, setShowLoginModal] = useState(false);
//   const [productToAdd, setProductToAdd] = useState(null);
//   const [imageErrors, setImageErrors] = useState({});

//   const navigate = useNavigate();
//   const dispatch = useDispatch();
//   const token = Cookies.get("ecom_user_token");
//   const cartItems = useSelector((state) => state.cart.items);
//   const { items: wishlist, loading: wishlistLoading } = useSelector(
//     (state) => state.wishlist
//   );

//   // Slider settings
//   const sliderSettings = {
//     dots: true,
//     infinite: true,
//     speed: 500,
//     slidesToShow: 4,
//     slidesToScroll: 1,
//     responsive: [
//       {
//         breakpoint: 1024,
//         settings: {
//           slidesToShow: 3,
//           slidesToScroll: 1,
//           infinite: true,
//           dots: true,
//         },
//       },
//       {
//         breakpoint: 768,
//         settings: {
//           slidesToShow: 2,
//           slidesToScroll: 1,
//           initialSlide: 2,
//         },
//       },
//       {
//         breakpoint: 480,
//         settings: {
//           slidesToShow: 1,
//           slidesToScroll: 1,
//         },
//       },
//     ],
//   };

//   useEffect(() => {
//     if (token) {
//       dispatch(fetchWishlist());
//     }
//   }, [token, dispatch]);

//   useEffect(() => {
//     dispatch(fetchCart());
//   }, [dispatch]);

//   const handleImageError = (productId) => {
//     setImageErrors((prev) => ({
//       ...prev,
//       [productId]: true,
//     }));
//   };

//   const getProductImage = (product) => {
//     if (imageErrors[product._id]) {
//       return fallbackImage;
//     }
//     return (
//       product.variants?.[0]?.images?.[0] || product.images?.[0] || fallbackImage
//     );
//   };

//   useEffect(() => {
//     const fetchTopRatedProducts = async () => {
//       try {
//         const response = await axios.get(
//           `${process.env.REACT_APP_BACKEND_URL}/api/seller/product/get-by-rating`
//         );

//         // Process products to find active variants only
//         const processedProducts = response?.data?.products
//           ?.map((item) => {
//             const product = item.product;

//             // Filter out non-active variants
//             const activeVariants = product.variants?.filter(
//               (v) => v.status === "active"
//             );

//             if (activeVariants.length === 0) return null; // Skip products with no active variants

//             // Create a new product object with only active variants
//             return {
//               ...item,
//               product: {
//                 ...product,
//                 variants: activeVariants,
//                 // Add a flag to indicate if the product is in stock
//                 isInStock: activeVariants.some(
//                   (v) => v.stock > 0 || v.stock === undefined
//                 ),
//               },
//             };
//           })
//           .filter(Boolean); // Remove null entries

//         // Sort products by rating (highest first) and then by number of ratings
//         const sortedProducts = processedProducts.sort((a, b) => {
//           if (b.averageRating !== a.averageRating) {
//             return b.averageRating - a.averageRating;
//           }
//           return b.ratingCount - a.ratingCount;
//         });

//         setProducts(sortedProducts);
//         // Take top 5 products for the slider
//         setTopRatedProducts(sortedProducts.slice(0, 5));
//         setLoading(false);
//       } catch (err) {
//         setError(err.message);
//         setLoading(false);
//         console.error("Error fetching top rated products:", err);
//       }
//     };

//     fetchTopRatedProducts();
//   }, []);

//   const fetchProductDetails = (productId) => {
//     navigate(`/product/${productId}`);
//   };

//   const handleAddToCart = async (product, e) => {
//     e.stopPropagation();

//     if (!token) {
//       setProductToAdd(product);
//       setShowLoginModal(true);
//       return;
//     }

//     const firstVariantId = product.variants?.[0]?._id;

//     if (!product.isInStock) {
//       toast.warning("This item is out of stock");
//       return;
//     }

//     try {
//       await dispatch(
//         addToCart({
//           productId: product._id,
//           variantId: product.variants?.length > 0 ? firstVariantId : undefined,
//           quantity: 1,
//         })
//       );
//       toast.success("Added to cart!");
//     } catch (error) {
//       console.error("Add to cart failed:", error);
//       toast.error(error.response?.data?.message || "Failed to add to cart");
//     }
//   };

//   const confirmAddToCart = async () => {
//     setShowLoginModal(false);
//     navigate("/login", { state: { from: window.location.pathname } });
//   };

//   const handleQuantityChange = async (productId, newQuantity, e) => {
//     e.stopPropagation();
//     const product = products.find((p) => p.product._id === productId)?.product;
//     const firstVariantId = product?.variants?.[0]?._id;

//     try {
//       await dispatch(
//         updateItemQuantity({
//           productId,
//           variantId: product?.variants?.length > 0 ? firstVariantId : undefined,
//           quantity: newQuantity,
//         })
//       );
//     } catch (error) {
//       console.error("Error updating quantity:", error);
//       toast.error("Failed to update quantity. Please try again.");
//     }
//   };

//   const toggleWishlist = async (productId, variantId, e) => {
//     e.stopPropagation();
//     if (!token) {
//       toast.info("Please login to manage your wishlist");
//       navigate("/login");
//       return;
//     }

//     try {
//       setLoadingWishlistItems((prev) => ({ ...prev, [productId]: true }));

//       const isInWishlist = wishlist.some(
//         (item) =>
//           item.productId._id === productId && item.variantId === variantId
//       );

//       if (isInWishlist) {
//         await dispatch(removeItemFromWishlist(productId, variantId));
//         toast.success("Removed from wishlist");
//       } else {
//         await dispatch(addItemToWishlist(productId, variantId));
//         toast.success("Added to wishlist");
//       }
//     } catch (error) {
//       console.error("Wishlist operation failed:", error);
//       toast.error(
//         error.response?.data?.message ||
//           "Failed to update wishlist. Please try again."
//       );
//     } finally {
//       setLoadingWishlistItems((prev) => ({ ...prev, [productId]: false }));
//     }
//   };

//   const renderProductActions = (product) => {
//     const firstVariant = product.variants?.[0];
//     const firstVariantId = firstVariant?._id;

//     if (!product.isInStock) {
//       return (
//         <button
//           className="px-4 py-2 rounded-full bg-gray-300 text-gray-600 cursor-not-allowed text-sm font-medium"
//           disabled
//         >
//           Out of Stock
//         </button>
//       );
//     }

//     const cartItem = cartItems.find(
//       (item) =>
//         item.productId === product._id &&
//         (product.variants?.length > 0
//           ? item.variantId === firstVariantId
//           : true)
//     );

//     if (cartItem) {
//       return (
//         <div className="flex items-center justify-center space-x-2">
//           <button
//             className={`w-8 h-8 flex items-center justify-center rounded-full transition-all duration-200 shadow-sm ${
//               cartItem.quantity <= 0
//                 ? "bg-gray-100 text-gray-400 cursor-not-allowed"
//                 : "bg-gradient-to-b from-gray-100 to-gray-200 hover:from-gray-200 hover:to-gray-300 text-gray-700 hover:text-gray-900 hover:shadow-md"
//             }`}
//             onClick={(e) => {
//               const newQuantity = cartItem.quantity - 1;
//               handleQuantityChange(product._id, newQuantity, e);
//             }}
//             disabled={cartItem.quantity <= 0}
//           >
//             <span className="text-lg font-medium">−</span>
//           </button>
//           <span className="w-8 text-center font-medium text-gray-700">
//             {cartItem.quantity}
//           </span>
//           <button
//             className={`w-8 h-8 flex items-center justify-center rounded-full bg-gradient-to-b from-blue-100 to-blue-200 hover:from-blue-200 hover:to-blue-300 text-blue-700 hover:text-blue-900 transition-all duration-200 shadow-sm hover:shadow-md ${
//               !product.isInStock ? "opacity-50 cursor-not-allowed" : ""
//             }`}
//             onClick={(e) =>
//               handleQuantityChange(product._id, cartItem.quantity + 1, e)
//             }
//             disabled={!product.isInStock}
//           >
//             <span className="text-lg font-medium">+</span>
//           </button>
//         </div>
//       );
//     } else {
//       return (
//         <button
//           className={`bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 transform hover:scale-105 shadow-md flex items-center ${
//             !product.isInStock ? "opacity-50 cursor-not-allowed" : ""
//           }`}
//           onClick={(e) => handleAddToCart(product, e)}
//           disabled={!product.isInStock}
//         >
//           <AiOutlineShoppingCart className="mr-2" />
//           Add to Cart
//         </button>
//       );
//     }
//   };

//   const renderRatingStars = (rating) => {
//     const stars = [];
//     const fullStars = Math.floor(rating);
//     const hasHalfStar = rating % 1 >= 0.5;

//     for (let i = 0; i < fullStars; i++) {
//       stars.push(<BsStarFill key={`full-${i}`} className="text-yellow-400" />);
//     }

//     if (hasHalfStar) {
//       stars.push(<BsStarHalf key="half" className="text-yellow-400" />);
//     }

//     const emptyStars = 5 - stars.length;
//     for (let i = 0; i < emptyStars; i++) {
//       stars.push(<BsStar key={`empty-${i}`} className="text-yellow-400" />);
//     }

//     return stars;
//   };

//   if (loading) {
//     return (
//       <div className="flex justify-center items-center h-96">
//         <PulseLoader color="#3B82F6" size={15} />
//       </div>
//     );
//   }

//   if (error) {
//     return (
//       <div className="text-red-500 text-center p-10 bg-red-50 rounded-xl max-w-3xl mx-auto my-12">
//         <h3 className="text-xl font-bold mb-2">Error Loading Products</h3>
//         <p>{error}</p>
//         <p className="mt-4">Please try again later</p>
//       </div>
//     );
//   }

//   if (topRatedProducts.length === 0) {
//     return (
//       <div className="text-gray-600 text-center p-10 bg-gray-50 rounded-xl max-w-3xl mx-auto my-12">
//         <h3 className="text-2xl font-bold mb-4">No Top Rated Products Found</h3>
//         <p>
//           We couldn't find any products with active variants and a 4+ star rating at the moment.
//         </p>
//       </div>
//     );
//   }

//   return (
//     <section className="py-16 bg-gradient-to-br from-blue-50 to-indigo-50">
//       {/* Login Confirmation Modal */}
//       {showLoginModal && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//           <div className="bg-white rounded-lg p-6 max-w-md w-full">
//             <h3 className="text-lg font-medium text-gray-900 mb-4">
//               Login Required
//             </h3>
//             <p className="text-gray-600 mb-6">
//               You need to login to add items to your cart. Would you like to
//               login now?
//             </p>
//             <div className="flex justify-end space-x-4">
//               <button
//                 onClick={() => setShowLoginModal(false)}
//                 className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
//               >
//                 Cancel
//               </button>
//               <button
//                 onClick={confirmAddToCart}
//                 className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
//               >
//                 Login
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       <div className="container mx-auto px-4">
//         <div className="text-center mb-12">
//           <div className="inline-block bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-1 rounded-full mb-3">
//             <span className="text-sm font-semibold">TOP RATED</span>
//           </div>
//           <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-3">
//             Customer <span className="text-blue-600">Favorites</span>
//           </h2>
//           <p className="text-lg text-gray-600 max-w-2xl mx-auto">
//             Discover our highest rated products loved by thousands of customers
//           </p>
//         </div>

//         <div className="px-4">
//           <Slider {...sliderSettings}>
//             {topRatedProducts.map((item, index) => {
//               const product = item.product;
//               const firstVariant = product.variants?.[0];
//               const firstVariantId = firstVariant?._id;
//               const productImage = getProductImage(product);

//               return (
//                 <div key={product._id} className="px-2">
//                   <div
//                     className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 relative h-full flex flex-col"
//                     onClick={() => fetchProductDetails(product._id)}
//                   >
//                     {/* Premium badge for top products */}
//                     {index < 2 && (
//                       <div className="absolute top-4 left-4 z-10">
//                         <div className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-white px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide shadow-md">
//                           {index === 0 ? "Best Seller" : "Popular"}
//                         </div>
//                       </div>
//                     )}

//                     {/* Wishlist button */}
//                     {token && (
//                       <button
//                         onClick={(e) =>
//                           toggleWishlist(product._id, firstVariantId, e)
//                         }
//                         disabled={loadingWishlistItems[product._id]}
//                         className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white bg-opacity-80 hover:bg-opacity-100 transition-all duration-200 shadow-sm"
//                       >
//                         {loadingWishlistItems[product._id] ? (
//                           <div className="h-5 w-5 flex items-center justify-center">
//                             <PulseLoader size={6} color="#EF4444" />
//                           </div>
//                         ) : wishlist.some(
//                             (item) =>
//                               item?.productId?._id === product?._id &&
//                               item?.variantId === firstVariantId
//                           ) ? (
//                           <AiFillHeart className="h-5 w-5 text-red-500 hover:text-red-700" />
//                         ) : (
//                           <AiOutlineHeart className="h-5 w-5 text-gray-500 hover:text-gray-700" />
//                         )}
//                       </button>
//                     )}

//                     {/* Product Image */}
//                     <div className="relative h-64 w-full overflow-hidden cursor-pointer bg-gray-100 flex items-center justify-center">
//                       <img
//                         src={productImage}
//                         alt={product.name}
//                         className="max-h-full max-w-full object-contain"
//                         onError={() => handleImageError(product._id)}
//                         loading="lazy"
//                       />
//                       {!product.isInStock && (
//                         <div className="absolute inset-0 bg-white bg-opacity-60 flex items-center justify-center">
//                           <span className="bg-gray-800 text-white px-3 py-1 rounded-md text-sm font-medium">
//                             Out of Stock
//                           </span>
//                         </div>
//                       )}
//                     </div>

//                     <div className="p-6 flex-grow flex flex-col">
//                       <div className="flex justify-between items-start mb-3">
//                         <div>
//                           <h3 className="text-xl font-bold text-gray-900 mb-1 line-clamp-1">
//                             {product.name}
//                           </h3>
//                           <div className="flex items-center">
//                             <div className="flex">
//                               {renderRatingStars(item.averageRating)}
//                             </div>
//                             <span className="ml-2 text-sm text-gray-600">
//                               ({item.ratingCount} review
//                               {item.ratingCount !== 1 ? "s" : ""})
//                             </span>
//                           </div>
//                         </div>
//                         <div className="text-right">
//                           <span className="text-2xl font-bold text-gray-900">
//                             ₹{firstVariant?.price?.toLocaleString()}
//                           </span>
//                           {firstVariant?.mrp > firstVariant?.price && (
//                             <div className="text-xs text-gray-500 line-through">
//                               ₹{firstVariant?.mrp?.toLocaleString()}
//                             </div>
//                           )}
//                         </div>
//                       </div>

//                       <p className="text-gray-600 mb-5 line-clamp-2 flex-grow truncate">
//                         {product.description}
//                       </p>

//                       <div className="flex justify-between items-center mt-auto">
//                         {!product.isInStock ? (
//                           <div className="text-sm font-medium text-red-600 bg-red-50 px-3 py-1 rounded-full">
//                             Out of Stock
//                           </div>
//                         ) : firstVariant?.stock <
//                           (firstVariant?.stock_threshold || 5) ? (
//                           <div className="text-sm font-medium text-orange-600 bg-orange-50 px-3 py-1 rounded-full">
//                             Only {firstVariant?.stock} left!
//                           </div>
//                         ) : (
//                           <div className="text-sm font-medium text-green-600 bg-green-50 px-3 py-1 rounded-full">
//                             In Stock
//                           </div>
//                         )}

//                         {renderProductActions(product)}
//                       </div>
//                     </div>
//                   </div>
//                 </div>
//               );
//             })}
//           </Slider>
//         </div>
//       </div>
//     </section>
//   );
// };

// export default TopRatedProducts;

// import React, { useEffect, useState } from "react";
// import axios from "axios";
// import Cookies from "js-cookie";
// import { useNavigate } from "react-router-dom";
// import { useDispatch, useSelector } from "react-redux";
// import {
//   updateItemQuantity,
//   addToCart,
//   fetchCart,
// } from "../../../redux/thunks/cartThunks";
// import {
//   fetchWishlist,
//   addItemToWishlist,
//   removeItemFromWishlist,
// } from "../../../redux/thunks/wishlistThunks";
// import Slider from "react-slick";
// import "slick-carousel/slick/slick.css";
// import "slick-carousel/slick/slick-theme.css";
// import {
//   AiOutlineHeart,
//   AiFillHeart,
//   AiOutlineShoppingCart,
// } from "react-icons/ai";
// import { BsStarFill, BsStarHalf, BsStar, BsStars } from "react-icons/bs";
// import { toast } from "react-toastify";
// import "react-toastify/dist/ReactToastify.css";
// import { PulseLoader } from "react-spinners";
// import fallbackImage from "../../../assets/brand-default.jpg";

// const TopRatedProducts = () => {
//   const [products, setProducts] = useState([]);
//   const [topRatedProducts, setTopRatedProducts] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [loadingWishlistItems, setLoadingWishlistItems] = useState({});
//   const [showLoginModal, setShowLoginModal] = useState(false);
//   const [productToAdd, setProductToAdd] = useState(null);
//   const [imageErrors, setImageErrors] = useState({});

//   const navigate = useNavigate();
//   const dispatch = useDispatch();
//   const token = Cookies.get("ecom_user_token");
//   const cartItems = useSelector((state) => state.cart.items);
//   const { items: wishlist, loading: wishlistLoading } = useSelector(
//     (state) => state.wishlist
//   );

//   // Slider settings
//   const sliderSettings = {
//     dots: true,
//     infinite: true,
//     speed: 500,
//     slidesToShow: 4,
//     slidesToScroll: 1,
//     responsive: [
//       {
//         breakpoint: 1024,
//         settings: {
//           slidesToShow: 3,
//           slidesToScroll: 1,
//           infinite: true,
//           dots: true,
//         },
//       },
//       {
//         breakpoint: 768,
//         settings: {
//           slidesToShow: 2,
//           slidesToScroll: 1,
//           initialSlide: 2,
//         },
//       },
//       {
//         breakpoint: 480,
//         settings: {
//           slidesToShow: 1,
//           slidesToScroll: 1,
//         },
//       },
//     ],
//   };

//   useEffect(() => {
//     if (token) {
//       dispatch(fetchWishlist());
//     }
//   }, [token, dispatch]);

//   useEffect(() => {
//     dispatch(fetchCart());
//   }, [dispatch]);

//   const handleImageError = (productId) => {
//     setImageErrors((prev) => ({
//       ...prev,
//       [productId]: true,
//     }));
//   };

//   const getProductImage = (product) => {
//     if (imageErrors[product._id]) {
//       return fallbackImage;
//     }
//     return (
//       product.variants?.[0]?.images?.[0] || product.images?.[0] || fallbackImage
//     );
//   };

//   useEffect(() => {
//     const fetchTopRatedProducts = async () => {
//       try {
//         const response = await axios.get(
//           `${process.env.REACT_APP_BACKEND_URL}/api/seller/product/get-by-rating`
//         );

//         // Process products to find the best available variant and add isInStock flag
//         const processedProducts = response?.data?.products
//           ?.map((item) => {
//             const product = item.product;

//             // Find variants in priority order
//             const activeVariant = product.variants?.find(
//               (v) => v.status === "active"
//             );
//             const outOfStockVariant = product.variants?.find(
//               (v) => v.status === "out_of_stock"
//             );

//             // Determine which variant to display (if any)
//             const displayVariant = activeVariant || outOfStockVariant;

//             if (!displayVariant) return null; // Skip products with no displayable variants

//             // Create a new product object with only the display variant
//             return {
//               ...item,
//               product: {
//                 ...product,
//                 variants: [displayVariant], // Keep only the display variant
//                 // Add a flag to indicate if the product is in stock
//                 isInStock:
//                   displayVariant.status === "active" &&
//                   (displayVariant.stock > 0 ||
//                     displayVariant.stock === undefined),
//               },
//             };
//           })
//           .filter(Boolean); // Remove null entries

//         // Sort products by rating (highest first) and then by number of ratings
//         const sortedProducts = processedProducts.sort((a, b) => {
//           if (b.averageRating !== a.averageRating) {
//             return b.averageRating - a.averageRating;
//           }
//           return b.ratingCount - a.ratingCount;
//         });

//         setProducts(sortedProducts);
//         // Take top 5 products for the slider
//         setTopRatedProducts(sortedProducts.slice(0, 5));
//         setLoading(false);
//       } catch (err) {
//         setError(err.message);
//         setLoading(false);
//         console.error("Error fetching top rated products:", err);
//       }
//     };

//     fetchTopRatedProducts();
//   }, []);

//   const fetchProductDetails = (productId) => {
//     navigate(`/product/${productId}`);
//   };

//   const handleAddToCart = async (product, e) => {
//     e.stopPropagation();

//     if (!token) {
//       setProductToAdd(product);
//       setShowLoginModal(true);
//       return;
//     }

//     const firstVariantId = product.variants?.[0]?._id;

//     if (!product.isInStock) {
//       toast.warning("This item is out of stock");
//       return;
//     }

//     try {
//       await dispatch(
//         addToCart({
//           productId: product._id,
//           variantId: product.variants?.length > 0 ? firstVariantId : undefined,
//           quantity: 1,
//         })
//       );
//       toast.success("Added to cart!");
//     } catch (error) {
//       console.error("Add to cart failed:", error);
//       toast.error(error.response?.data?.message || "Failed to add to cart");
//     }
//   };

//   const confirmAddToCart = async () => {
//     setShowLoginModal(false);
//     navigate("/login", { state: { from: window.location.pathname } });
//   };

//   const handleQuantityChange = async (productId, newQuantity, e) => {
//     e.stopPropagation();
//     const product = products.find((p) => p.product._id === productId)?.product;
//     const firstVariantId = product?.variants?.[0]?._id;

//     try {
//       await dispatch(
//         updateItemQuantity({
//           productId,
//           variantId: product?.variants?.length > 0 ? firstVariantId : undefined,
//           quantity: newQuantity,
//         })
//       );
//     } catch (error) {
//       console.error("Error updating quantity:", error);
//       toast.error("Failed to update quantity. Please try again.");
//     }
//   };

//   const toggleWishlist = async (productId, variantId, e) => {
//     e.stopPropagation();
//     if (!token) {
//       toast.info("Please login to manage your wishlist");
//       navigate("/login");
//       return;
//     }

//     try {
//       setLoadingWishlistItems((prev) => ({ ...prev, [productId]: true }));

//       const isInWishlist = wishlist.some(
//         (item) =>
//           item.productId._id === productId && item.variantId === variantId
//       );

//       if (isInWishlist) {
//         await dispatch(removeItemFromWishlist(productId, variantId));
//         toast.success("Removed from wishlist");
//       } else {
//         await dispatch(addItemToWishlist(productId, variantId));
//         toast.success("Added to wishlist");
//       }
//     } catch (error) {
//       console.error("Wishlist operation failed:", error);
//       toast.error(
//         error.response?.data?.message ||
//           "Failed to update wishlist. Please try again."
//       );
//     } finally {
//       setLoadingWishlistItems((prev) => ({ ...prev, [productId]: false }));
//     }
//   };

//   const renderProductActions = (product) => {
//     const firstVariant = product.variants?.[0];
//     const firstVariantId = firstVariant?._id;

//     if (!product.isInStock) {
//       return (
//         <button
//           className="px-4 py-2 rounded-full bg-gray-300 text-gray-600 cursor-not-allowed text-sm font-medium"
//           disabled
//         >
//           Out of Stock
//         </button>
//       );
//     }

//     const cartItem = cartItems.find(
//       (item) =>
//         item.productId === product._id &&
//         (product.variants?.length > 0
//           ? item.variantId === firstVariantId
//           : true)
//     );

//     if (cartItem) {
//       return (
//         <div className="flex items-center justify-center space-x-2">
//           <button
//             className={`w-8 h-8 flex items-center justify-center rounded-full transition-all duration-200 shadow-sm ${
//               cartItem.quantity <= 0
//                 ? "bg-gray-100 text-gray-400 cursor-not-allowed"
//                 : "bg-gradient-to-b from-gray-100 to-gray-200 hover:from-gray-200 hover:to-gray-300 text-gray-700 hover:text-gray-900 hover:shadow-md"
//             }`}
//             onClick={(e) => {
//               const newQuantity = cartItem.quantity - 1;
//               handleQuantityChange(product._id, newQuantity, e);
//             }}
//             disabled={cartItem.quantity <= 0}
//           >
//             <span className="text-lg font-medium">−</span>
//           </button>
//           <span className="w-8 text-center font-medium text-gray-700">
//             {cartItem.quantity}
//           </span>
//           <button
//             className={`w-8 h-8 flex items-center justify-center rounded-full bg-gradient-to-b from-blue-100 to-blue-200 hover:from-blue-200 hover:to-blue-300 text-blue-700 hover:text-blue-900 transition-all duration-200 shadow-sm hover:shadow-md ${
//               !product.isInStock ? "opacity-50 cursor-not-allowed" : ""
//             }`}
//             onClick={(e) =>
//               handleQuantityChange(product._id, cartItem.quantity + 1, e)
//             }
//             disabled={!product.isInStock}
//           >
//             <span className="text-lg font-medium">+</span>
//           </button>
//         </div>
//       );
//     } else {
//       return (
//         <button
//           className={`bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 transform hover:scale-105 shadow-md flex items-center ${
//             !product.isInStock ? "opacity-50 cursor-not-allowed" : ""
//           }`}
//           onClick={(e) => handleAddToCart(product, e)}
//           disabled={!product.isInStock}
//         >
//           <AiOutlineShoppingCart className="mr-2" />
//           Add to Cart
//         </button>
//       );
//     }
//   };

//   const renderRatingStars = (rating) => {
//     const stars = [];
//     const fullStars = Math.floor(rating);
//     const hasHalfStar = rating % 1 >= 0.5;

//     for (let i = 0; i < fullStars; i++) {
//       stars.push(<BsStarFill key={`full-${i}`} className="text-yellow-400" />);
//     }

//     if (hasHalfStar) {
//       stars.push(<BsStarHalf key="half" className="text-yellow-400" />);
//     }

//     const emptyStars = 5 - stars.length;
//     for (let i = 0; i < emptyStars; i++) {
//       stars.push(<BsStar key={`empty-${i}`} className="text-yellow-400" />);
//     }

//     return stars;
//   };

//   if (loading) {
//     return (
//       <div className="flex justify-center items-center h-96">
//         <PulseLoader color="#3B82F6" size={15} />
//       </div>
//     );
//   }

//   if (error) {
//     return (
//       <div className="text-red-500 text-center p-10 bg-red-50 rounded-xl max-w-3xl mx-auto my-12">
//         <h3 className="text-xl font-bold mb-2">Error Loading Products</h3>
//         <p>{error}</p>
//         <p className="mt-4">Please try again later</p>
//       </div>
//     );
//   }

//   if (topRatedProducts.length === 0) {
//     return (
//       <div className="text-gray-600 text-center p-10 bg-gray-50 rounded-xl max-w-3xl mx-auto my-12">
//         <h3 className="text-2xl font-bold mb-4">No Top Rated Products Found</h3>
//         <p>
//           We couldn't find any products with a 4+ star rating at the moment.
//         </p>
//       </div>
//     );
//   }

//   return (
//     <section className="py-16 bg-gradient-to-br from-blue-50 to-indigo-50">
//       {/* Login Confirmation Modal */}
//       {showLoginModal && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//           <div className="bg-white rounded-lg p-6 max-w-md w-full">
//             <h3 className="text-lg font-medium text-gray-900 mb-4">
//               Login Required
//             </h3>
//             <p className="text-gray-600 mb-6">
//               You need to login to add items to your cart. Would you like to
//               login now?
//             </p>
//             <div className="flex justify-end space-x-4">
//               <button
//                 onClick={() => setShowLoginModal(false)}
//                 className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
//               >
//                 Cancel
//               </button>
//               <button
//                 onClick={confirmAddToCart}
//                 className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
//               >
//                 Login
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       <div className="container mx-auto px-4">
//         <div className="text-center mb-12">
//           <div className="inline-block bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-1 rounded-full mb-3">
//             <span className="text-sm font-semibold">TOP RATED</span>
//           </div>
//           <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-3">
//             Customer <span className="text-blue-600">Favorites</span>
//           </h2>
//           <p className="text-lg text-gray-600 max-w-2xl mx-auto">
//             Discover our highest rated products loved by thousands of customers
//           </p>
//         </div>

//         <div className="px-4">
//           <Slider {...sliderSettings}>
//             {topRatedProducts.map((item, index) => {
//               const product = item.product;
//               const firstVariant = product.variants?.[0];
//               const firstVariantId = firstVariant?._id;
//               const productImage = getProductImage(product);

//               return (
//                 <div key={product._id} className="px-2">
//                   <div
//                     className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 relative h-full flex flex-col"
//                     onClick={() => fetchProductDetails(product._id)}
//                   >
//                     {/* Premium badge for top products */}
//                     {index < 2 && (
//                       <div className="absolute top-4 left-4 z-10">
//                         <div className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-white px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide shadow-md">
//                           {index === 0 ? "Best Seller" : "Popular"}
//                         </div>
//                       </div>
//                     )}

//                     {/* Wishlist button */}
//                     {token && (
//                       <button
//                         onClick={(e) =>
//                           toggleWishlist(product._id, firstVariantId, e)
//                         }
//                         disabled={loadingWishlistItems[product._id]}
//                         className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white bg-opacity-80 hover:bg-opacity-100 transition-all duration-200 shadow-sm"
//                       >
//                         {loadingWishlistItems[product._id] ? (
//                           <div className="h-5 w-5 flex items-center justify-center">
//                             <PulseLoader size={6} color="#EF4444" />
//                           </div>
//                         ) : wishlist.some(
//                             (item) =>
//                               item?.productId?._id === product?._id &&
//                               item?.variantId === firstVariantId
//                           ) ? (
//                           <AiFillHeart className="h-5 w-5 text-red-500 hover:text-red-700" />
//                         ) : (
//                           <AiOutlineHeart className="h-5 w-5 text-gray-500 hover:text-gray-700" />
//                         )}
//                       </button>
//                     )}

//                     {/* Rating badge */}
//                     {/* <div className="absolute top-16 right-4 z-10 bg-blue-600 text-white rounded-full w-12 h-12 flex flex-col items-center justify-center shadow-lg">
//                       <span className="text-lg font-bold">{item?.averageRating?.toFixed(1)}</span>
//                       <div className="flex -mt-1">
//                         <BsStarFill className="w-3 h-3 text-yellow-300" />
//                       </div>
//                     </div> */}

//                     {/* Product Image */}
//                     <div className="relative h-64 w-full overflow-hidden cursor-pointer bg-gray-100 flex items-center justify-center">
//                       <img
//                         src={productImage}
//                         alt={product.name}
//                         className="max-h-full max-w-full object-contain"
//                         onError={() => handleImageError(product._id)}
//                         loading="lazy"
//                       />
//                       {!product.isInStock && (
//                         <div className="absolute inset-0 bg-white bg-opacity-60 flex items-center justify-center">
//                           <span className="bg-gray-800 text-white px-3 py-1 rounded-md text-sm font-medium">
//                             Out of Stock
//                           </span>
//                         </div>
//                       )}
//                     </div>

//                     <div className="p-6 flex-grow flex flex-col">
//                       <div className="flex justify-between items-start mb-3">
//                         <div>
//                           <h3 className="text-xl font-bold text-gray-900 mb-1 line-clamp-1">
//                             {product.name}
//                           </h3>
//                           <div className="flex items-center">
//                             <div className="flex">
//                               {renderRatingStars(item.averageRating)}
//                             </div>
//                             <span className="ml-2 text-sm text-gray-600">
//                               ({item.ratingCount} review
//                               {item.ratingCount !== 1 ? "s" : ""})
//                             </span>
//                           </div>
//                         </div>
//                         <div className="text-right">
//                           <span className="text-2xl font-bold text-gray-900">
//                             ₹{firstVariant?.price?.toLocaleString()}
//                           </span>
//                           {firstVariant?.mrp > firstVariant?.price && (
//                             <div className="text-xs text-gray-500 line-through">
//                               ₹{firstVariant?.mrp?.toLocaleString()}
//                             </div>
//                           )}
//                         </div>
//                       </div>

//                       <p className="text-gray-600 mb-5 line-clamp-2 flex-grow truncate">
//                         {product.description}
//                       </p>

//                       <div className="flex justify-between items-center mt-auto">
//                         {!product.isInStock ? (
//                           <div className="text-sm font-medium text-red-600 bg-red-50 px-3 py-1 rounded-full">
//                             Out of Stock
//                           </div>
//                         ) : firstVariant?.stock <
//                           (firstVariant?.stock_threshold || 5) ? (
//                           <div className="text-sm font-medium text-orange-600 bg-orange-50 px-3 py-1 rounded-full">
//                             Only {firstVariant?.stock} left!
//                           </div>
//                         ) : (
//                           <div className="text-sm font-medium text-green-600 bg-green-50 px-3 py-1 rounded-full">
//                             In Stock
//                           </div>
//                         )}

//                         {renderProductActions(product)}
//                       </div>
//                     </div>
//                   </div>
//                 </div>
//               );
//             })}
//           </Slider>
//         </div>
//       </div>
//     </section>
//   );
// };

// export default TopRatedProducts;

// import React, { useEffect, useState } from 'react';
// import axios from 'axios';
// import Cookies from 'js-cookie';
// import { useNavigate } from 'react-router-dom';
// import { useDispatch, useSelector } from 'react-redux';
// import { updateItemQuantity, addToCart, fetchCart } from '../../../redux/thunks/cartThunks';
// import {
//   fetchWishlist,
//   addItemToWishlist,
//   removeItemFromWishlist
// } from '../../../redux/thunks/wishlistThunks';
// import Slider from 'react-slick';
// import 'slick-carousel/slick/slick.css';
// import 'slick-carousel/slick/slick-theme.css';
// import { AiOutlineHeart, AiFillHeart, AiOutlineShoppingCart } from 'react-icons/ai';
// import { BsStarFill, BsStars } from 'react-icons/bs';
// import { toast } from 'react-toastify';
// import 'react-toastify/dist/ReactToastify.css';
// import { PulseLoader } from 'react-spinners';

// const TopRatedProducts = () => {
//   const [products, setProducts] = useState([]);
//   const [topRatedProducts, setTopRatedProducts] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [loadingWishlistItems, setLoadingWishlistItems] = useState({});
//   const [showLoginModal, setShowLoginModal] = useState(false);
//   const [productToAdd, setProductToAdd] = useState(null);

//   const navigate = useNavigate();
//   const dispatch = useDispatch();
//   const token = Cookies.get("ecom_user_token");
//   const cartItems = useSelector(state => state.cart.items);
//   const { items: wishlist, loading: wishlistLoading } = useSelector(state => state.wishlist);

//   // Slider settings
//   const sliderSettings = {
//     dots: true,
//     infinite: true,
//     speed: 500,
//     slidesToShow: 4,
//     slidesToScroll: 1,
//     responsive: [
//       {
//         breakpoint: 1024,
//         settings: {
//           slidesToShow: 3,
//           slidesToScroll: 1,
//           infinite: true,
//           dots: true
//         }
//       },
//       {
//         breakpoint: 768,
//         settings: {
//           slidesToShow: 2,
//           slidesToScroll: 1,
//           initialSlide: 2
//         }
//       },
//       {
//         breakpoint: 480,
//         settings: {
//           slidesToShow: 1,
//           slidesToScroll: 1
//         }
//       }
//     ]
//   };

//   useEffect(() => {
//     if (token) {
//       dispatch(fetchWishlist());
//     }
//   }, [token, dispatch]);

//   useEffect(() => {
//     dispatch(fetchCart());
//   }, [dispatch]);

//   useEffect(() => {
//     const fetchTopRatedProducts = async () => {
//       try {
//         const response = await axios.get(
//           `${process.env.REACT_APP_BACKEND_URL}/api/seller/product/get-by-rating`,
//         );

//         // Process products to find the best available variant and add isInStock flag
//         const processedProducts = response?.data?.products?.map(item => {
//           const product = item.product;

//           // Find variants in priority order
//           const activeVariant = product.variants?.find(v => v.status === "active");
//           const outOfStockVariant = product.variants?.find(v => v.status === "out_of_stock");

//           // Determine which variant to display (if any)
//           const displayVariant = activeVariant || outOfStockVariant;

//           if (!displayVariant) return null; // Skip products with no displayable variants

//           // Create a new product object with only the display variant
//           return {
//             ...item,
//             product: {
//               ...product,
//               variants: [displayVariant], // Keep only the display variant
//               // Add a flag to indicate if the product is in stock
//               isInStock: displayVariant.status === "active" &&
//                         (displayVariant.stock > 0 || displayVariant.stock === undefined)
//             }
//           };
//         }).filter(Boolean); // Remove null entries

//         // Sort products by rating (highest first) and then by number of ratings
//         const sortedProducts = processedProducts.sort((a, b) => {
//           if (b.averageRating !== a.averageRating) {
//             return b.averageRating - a.averageRating;
//           }
//           return b.ratingCount - a.ratingCount;
//         });

//         setProducts(sortedProducts);
//         // Take top 5 products for the slider
//         setTopRatedProducts(sortedProducts.slice(0, 5));
//         setLoading(false);
//       } catch (err) {
//         setError(err.message);
//         setLoading(false);
//         console.error("Error fetching top rated products:", err);
//       }
//     };

//     fetchTopRatedProducts();
//   }, []);

//   const fetchProductDetails = (productId) => {
//     navigate(`/product/${productId}`);
//   };

//   const handleAddToCart = async (product, e) => {
//     e.stopPropagation();

//     if (!token) {
//       setProductToAdd(product);
//       setShowLoginModal(true);
//       return;
//     }

//     const firstVariantId = product.variants?.[0]?._id;

//     if (!product.isInStock) {
//       toast.warning("This item is out of stock");
//       return;
//     }

//     try {
//       await dispatch(addToCart({
//         productId: product._id,
//         variantId: product.variants?.length > 0 ? firstVariantId : undefined,
//         quantity: 1
//       }));
//       toast.success("Added to cart!");
//     } catch (error) {
//       console.error("Add to cart failed:", error);
//       toast.error(error.response?.data?.message || "Failed to add to cart");
//     }
//   };

//   const confirmAddToCart = async () => {
//     setShowLoginModal(false);
//     navigate('/login', { state: { from: window.location.pathname } });
//   };

//   const handleQuantityChange = async (productId, newQuantity, e) => {
//     e.stopPropagation();
//     const product = products.find(p => p.product._id === productId)?.product;
//     const firstVariantId = product?.variants?.[0]?._id;

//     try {
//       await dispatch(updateItemQuantity({
//         productId,
//         variantId: product?.variants?.length > 0 ? firstVariantId : undefined,
//         quantity: newQuantity
//       }));
//     } catch (error) {
//       console.error("Error updating quantity:", error);
//       toast.error("Failed to update quantity. Please try again.");
//     }
//   };

//   const toggleWishlist = async (productId, variantId, e) => {
//     e.stopPropagation();
//     if (!token) {
//       toast.info("Please login to manage your wishlist");
//       navigate('/login');
//       return;
//     }

//     try {
//       setLoadingWishlistItems(prev => ({ ...prev, [productId]: true }));

//       const isInWishlist = wishlist.some(item =>
//         item.productId._id === productId &&
//         item.variantId === variantId
//       );

//       if (isInWishlist) {
//         await dispatch(removeItemFromWishlist(productId, variantId));
//         toast.success("Removed from wishlist");
//       } else {
//         await dispatch(addItemToWishlist(productId, variantId));
//         toast.success("Added to wishlist");
//       }
//     } catch (error) {
//       console.error("Wishlist operation failed:", error);
//       toast.error(error.response?.data?.message || "Failed to update wishlist. Please try again.");
//     } finally {
//       setLoadingWishlistItems(prev => ({ ...prev, [productId]: false }));
//     }
//   };

//   const renderProductActions = (product) => {
//     const firstVariant = product.variants?.[0];
//     const firstVariantId = firstVariant?._id;

//     if (!product.isInStock) {
//       return (
//         <button
//           className="px-4 py-2 rounded-full bg-gray-300 text-gray-600 cursor-not-allowed text-sm font-medium"
//           disabled
//         >
//           Out of Stock
//         </button>
//       );
//     }

//     const cartItem = cartItems.find(item =>
//       item.productId === product._id &&
//       (product.variants?.length > 0 ? item.variantId === firstVariantId : true)
//     );

//     if (cartItem) {
//       return (
//         <div className="flex items-center justify-center space-x-2">
//           <button
//             className={`w-8 h-8 flex items-center justify-center rounded-full transition-all duration-200 shadow-sm ${
//               cartItem.quantity <= 0
//                 ? "bg-gray-100 text-gray-400 cursor-not-allowed"
//                 : "bg-gradient-to-b from-gray-100 to-gray-200 hover:from-gray-200 hover:to-gray-300 text-gray-700 hover:text-gray-900 hover:shadow-md"
//             }`}
//             onClick={(e) => {
//               const newQuantity = cartItem.quantity - 1;
//               handleQuantityChange(product._id, newQuantity, e);
//             }}
//             disabled={cartItem.quantity <= 0}
//           >
//             <span className="text-lg font-medium">−</span>
//           </button>
//           <span className="w-8 text-center font-medium text-gray-700">
//             {cartItem.quantity}
//           </span>
//           <button
//             className={`w-8 h-8 flex items-center justify-center rounded-full bg-gradient-to-b from-blue-100 to-blue-200 hover:from-blue-200 hover:to-blue-300 text-blue-700 hover:text-blue-900 transition-all duration-200 shadow-sm hover:shadow-md ${
//               !product.isInStock ? 'opacity-50 cursor-not-allowed' : ''
//             }`}
//             onClick={(e) => handleQuantityChange(product._id, cartItem.quantity + 1, e)}
//             disabled={!product.isInStock}
//           >
//             <span className="text-lg font-medium">+</span>
//           </button>
//         </div>
//       );
//     } else {
//       return (
//         <button
//           className={`bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 transform hover:scale-105 shadow-md flex items-center ${
//             !product.isInStock ? 'opacity-50 cursor-not-allowed' : ''
//           }`}
//           onClick={(e) => handleAddToCart(product, e)}
//           disabled={!product.isInStock}
//         >
//           <AiOutlineShoppingCart className="mr-2" />
//           Add to Cart
//         </button>
//       );
//     }
//   };

//   const renderRatingStars = (rating) => {
//     const stars = [];
//     const fullStars = Math.floor(rating);
//     const hasHalfStar = rating % 1 >= 0.5;

//     for (let i = 0; i < fullStars; i++) {
//       stars.push(<BsStarFill key={`full-${i}`} className="text-yellow-400" />);
//     }

//     if (hasHalfStar) {
//       stars.push(<BsStarHalf key="half" className="text-yellow-400" />);
//     }

//     const emptyStars = 5 - stars.length;
//     for (let i = 0; i < emptyStars; i++) {
//       stars.push(<BsStar key={`empty-${i}`} className="text-yellow-400" />);
//     }

//     return stars;
//   };

//   if (loading) {
//     return (
//       <div className="flex justify-center items-center h-96">
//         <PulseLoader color="#3B82F6" size={15} />
//       </div>
//     );
//   }

//   if (error) {
//     return (
//       <div className="text-red-500 text-center p-10 bg-red-50 rounded-xl max-w-3xl mx-auto my-12">
//         <h3 className="text-xl font-bold mb-2">Error Loading Products</h3>
//         <p>{error}</p>
//         <p className="mt-4">Please try again later</p>
//       </div>
//     );
//   }

//   if (topRatedProducts.length === 0) {
//     return (
//       <div className="text-gray-600 text-center p-10 bg-gray-50 rounded-xl max-w-3xl mx-auto my-12">
//         <h3 className="text-2xl font-bold mb-4">No Top Rated Products Found</h3>
//         <p>We couldn't find any products with a 4+ star rating at the moment.</p>
//       </div>
//     );
//   }

//   return (
//     <section className="py-16 bg-gradient-to-br from-blue-50 to-indigo-50">
//       {/* Login Confirmation Modal */}
//       {showLoginModal && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//           <div className="bg-white rounded-lg p-6 max-w-md w-full">
//             <h3 className="text-lg font-medium text-gray-900 mb-4">Login Required</h3>
//             <p className="text-gray-600 mb-6">
//               You need to login to add items to your cart. Would you like to login now?
//             </p>
//             <div className="flex justify-end space-x-4">
//               <button
//                 onClick={() => setShowLoginModal(false)}
//                 className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
//               >
//                 Cancel
//               </button>
//               <button
//                 onClick={confirmAddToCart}
//                 className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
//               >
//                 Login
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       <div className="container mx-auto px-4">
//         <div className="text-center mb-12">
//           <div className="inline-block bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-1 rounded-full mb-3">
//             <span className="text-sm font-semibold">TOP RATED</span>
//           </div>
//           <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-3">
//             Customer <span className="text-blue-600">Favorites</span>
//           </h2>
//           <p className="text-lg text-gray-600 max-w-2xl mx-auto">
//             Discover our highest rated products loved by thousands of customers
//           </p>
//         </div>

//         <div className="px-4">
//           <Slider {...sliderSettings}>
//             {topRatedProducts.map((item, index) => {
//               const product = item.product;
//               const firstVariant = product.variants?.[0];
//               const firstVariantId = firstVariant?._id;

//               return (
//                 <div key={product._id} className="px-2">
//                   <div
//                     className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 relative h-full"
//                     onClick={() => fetchProductDetails(product._id)}
//                   >
//                     {/* Premium badge for top products */}
//                     {index < 2 && (
//                       <div className="absolute top-4 left-4 z-10">
//                         <div className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-white px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide shadow-md">
//                           {index === 0 ? 'Best Seller' : 'Popular'}
//                         </div>
//                       </div>
//                     )}

//                     {/* Wishlist button */}
//                     {token && (
//                       <button
//                         onClick={(e) => toggleWishlist(product._id, firstVariantId, e)}
//                         disabled={loadingWishlistItems[product._id]}
//                         className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white bg-opacity-80 hover:bg-opacity-100 transition-all duration-200 shadow-sm"
//                       >
//                         {loadingWishlistItems[product._id] ? (
//                           <div className="h-5 w-5 flex items-center justify-center">
//                             <PulseLoader size={6} color="#EF4444" />
//                           </div>
//                         ) : wishlist.some(item =>
//                             item?.productId?._id === product?._id &&
//                             item?.variantId === firstVariantId
//                           ) ? (
//                           <AiFillHeart className="h-5 w-5 text-red-500 hover:text-red-700" />
//                         ) : (
//                           <AiOutlineHeart className="h-5 w-5 text-gray-500 hover:text-gray-700" />
//                         )}
//                       </button>
//                     )}

//                     {/* Rating badge */}
//                     <div className="absolute top-16 right-4 z-10 bg-blue-600 text-white rounded-full w-12 h-12 flex flex-col items-center justify-center shadow-lg">
//                       <span className="text-lg font-bold">{item?.averageRating?.toFixed(1)}</span>
//                       <div className="flex -mt-1">
//                         <BsStarFill className="w-3 h-3 text-yellow-300" />
//                       </div>
//                     </div>

//                     <div className="relative h-64 overflow-hidden cursor-pointer">
//                       {firstVariant?.images?.[0] ? (
//                         <img
//                           src={firstVariant.images[0]}
//                           alt={product.name}
//                           className="w-full h-full object-cover transition-transform duration-500 hover:scale-110 cursor-pointer"
//                           onError={(e) => {
//                             e.target.src = "../../../assets/brand-default.jpg";
//                             e.target.className = "w-full h-full object-contain p-4 bg-gray-100";
//                           }}
//                           loading='lazy'
//                         />
//                       ) : (
//                         <div className="w-full h-full bg-gray-100 flex items-center justify-center">
//                           <span className="text-gray-400">No Image Available</span>
//                         </div>
//                       )}
//                     </div>

//                     <div className="p-6">
//                       <div className="flex justify-between items-start mb-3">
//                         <div>
//                           <h3 className="text-xl font-bold text-gray-900 mb-1">{product.name}</h3>
//                           <div className="flex items-center">
//                             <div className="flex">
//                               {renderRatingStars(item.averageRating)}
//                             </div>
//                             <span className="ml-2 text-sm text-gray-600">
//                               ({item.ratingCount} review{item.ratingCount !== 1 ? 's' : ''})
//                             </span>
//                           </div>
//                         </div>
//                         <div className="text-right">
//                           <span className="text-2xl font-bold text-gray-900">
//                             ₹{firstVariant?.price?.toLocaleString()}
//                           </span>
//                           {firstVariant?.mrp > firstVariant?.price && (
//                             <div className="text-xs text-gray-500 line-through">
//                               ₹{firstVariant?.mrp?.toLocaleString()}
//                             </div>
//                           )}
//                         </div>
//                       </div>

//                       <p className="text-gray-600 mb-5 line-clamp-2 h-12">
//                         {product.description}
//                       </p>

//                       <div className="flex justify-between items-center">
//                         {!product.isInStock ? (
//                           <div className="text-sm font-medium text-red-600 bg-red-50 px-3 py-1 rounded-full">
//                             Out of Stock
//                           </div>
//                         ) : firstVariant?.stock < (firstVariant?.stock_threshold || 5) ? (
//                           <div className="text-sm font-medium text-orange-600 bg-orange-50 px-3 py-1 rounded-full">
//                             Only {firstVariant?.stock} left!
//                           </div>
//                         ) : (
//                           <div className="text-sm font-medium text-green-600 bg-green-50 px-3 py-1 rounded-full">
//                             In Stock
//                           </div>
//                         )}

//                         {renderProductActions(product)}
//                       </div>
//                     </div>
//                   </div>
//                 </div>
//               );
//             })}
//           </Slider>
//         </div>
//       </div>
//     </section>
//   );
// };

// export default TopRatedProducts;

// import React, { useEffect, useState } from 'react';
// import axios from 'axios';
// import Cookies from 'js-cookie';
// import { useNavigate } from 'react-router-dom';
// import { useDispatch, useSelector } from 'react-redux';
// import { updateItemQuantity, addToCart } from '../../../redux/thunks/cartThunks';
// import Slider from 'react-slick';
// import 'slick-carousel/slick/slick.css';
// import 'slick-carousel/slick/slick-theme.css';

// const TopRatedProducts = () => {
//   const [products, setProducts] = useState([]);
//   const [topRatedProducts, setTopRatedProducts] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const navigate = useNavigate();
//   const dispatch = useDispatch();
//   const cartItems = useSelector(state => state.cart.items);

//   // Slider settings
//   const sliderSettings = {
//     dots: true,
//     infinite: true,
//     speed: 500,
//     slidesToShow: 4,
//     slidesToScroll: 1,
//     responsive: [
//       {
//         breakpoint: 1024,
//         settings: {
//           slidesToShow: 3,
//           slidesToScroll: 1,
//           infinite: true,
//           dots: true
//         }
//       },
//       {
//         breakpoint: 768,
//         settings: {
//           slidesToShow: 2,
//           slidesToScroll: 1,
//           initialSlide: 2
//         }
//       },
//       {
//         breakpoint: 480,
//         settings: {
//           slidesToShow: 1,
//           slidesToScroll: 1
//         }
//       }
//     ]
//   };

//   useEffect(() => {
//     const fetchTopRatedProducts = async () => {
//       try {
//         const response = await axios.get(
//           `${process.env.REACT_APP_BACKEND_URL}/api/seller/product/get-by-rating`,
//         );

//         // Sort products by rating (highest first) and then by number of ratings
//         const sortedProducts = response?.data?.products?.sort((a, b) => {
//           if (b.averageRating !== a.averageRating) {
//             return b.averageRating - a.averageRating;
//           }
//           return b.ratingCount - a.ratingCount;
//         });

//         setProducts(sortedProducts);
//         // Take top 5 products for the slider
//         setTopRatedProducts(sortedProducts.slice(0, 5));
//         setLoading(false);
//       } catch (err) {
//         setError(err.message);
//         setLoading(false);
//         console.error("Error fetching top rated products:", err);
//       }
//     };

//     fetchTopRatedProducts();
//   }, []);

//   const fetchProductDetails = (productId) => {
//     navigate(`/product/${productId}`);
//   };

//   const handleAddToCart = async (product) => {
//     if (product.stock <= 0) {
//       alert("This product is out of stock");
//       return;
//     }

//     try {
//       await dispatch(addToCart({
//         productId: product._id,
//         quantity: 1
//       }));
//     } catch (error) {
//       console.error("Add to cart failed:", error);
//       alert("Failed to add to cart. Please try again.");
//     }
//   };

//   const handleQuantityChange = async (productId, newQuantity) => {
//     try {
//       await dispatch(updateItemQuantity({ productId, quantity: newQuantity }));
//     } catch (error) {
//       console.error("Error updating quantity:", error);
//       alert("Failed to update quantity. Please try again.");
//     }
//   };

//   const renderProductActions = (product) => {
//     const cartItem = cartItems.find(item => item.productId === product._id);

//     if (cartItem) {
//       return (
//         <div className="flex items-center justify-center space-x-2">
//           <button
//             className={`w-8 h-8 flex items-center justify-center rounded-full transition-all duration-200 shadow-sm ${
//               cartItem.quantity <= 0
//                 ? "bg-gray-100 text-gray-400 cursor-not-allowed"
//                 : "bg-gradient-to-b from-gray-100 to-gray-200 hover:from-gray-200 hover:to-gray-300 text-gray-700 hover:text-gray-900 hover:shadow-md"
//             }`}
//             onClick={(e) => {
//               e.stopPropagation();
//               const newQuantity = cartItem.quantity - 1;
//               handleQuantityChange(product._id, newQuantity);
//             }}
//             disabled={cartItem.quantity <= 0}
//           >
//             <span className="text-lg font-medium">−</span>
//           </button>
//           <span className="w-8 text-center font-medium text-gray-700">
//             {cartItem.quantity}
//           </span>
//           <button
//             className="w-8 h-8 flex items-center justify-center rounded-full bg-gradient-to-b from-blue-100 to-blue-200 hover:from-blue-200 hover:to-blue-300 text-blue-700 hover:text-blue-900 transition-all duration-200 shadow-sm hover:shadow-md"
//             onClick={(e) => {
//               e.stopPropagation();
//               handleQuantityChange(product._id, cartItem.quantity + 1);
//             }}
//           >
//             <span className="text-lg font-medium">+</span>
//           </button>
//         </div>
//       );
//     } else {
//       return (
//         <button
//           className="bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white px-5 py-2 rounded-full font-medium transition-all duration-300 transform hover:scale-105 shadow-md flex items-center"
//           onClick={(e) => {
//             e.stopPropagation();
//             handleAddToCart(product);
//           }}
//           disabled={product?.stock <= 0}
//         >
//           <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"></path>
//           </svg>
//           Add to Cart
//         </button>
//       );
//     }
//   };

//   if (loading) {
//     return (
//       <div className="flex justify-center items-center h-96">
//         <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-600"></div>
//       </div>
//     );
//   }

//   if (error) {
//     return (
//       <div className="text-red-500 text-center p-10 bg-red-50 rounded-xl max-w-3xl mx-auto my-12">
//         <h3 className="text-xl font-bold mb-2">Error Loading Products</h3>
//         <p>{error}</p>
//         <p className="mt-4">Please try again later</p>
//       </div>
//     );
//   }

//   if (topRatedProducts.length === 0) {
//     return (
//       <div className="text-gray-600 text-center p-10 bg-gray-50 rounded-xl max-w-3xl mx-auto my-12">
//         <h3 className="text-2xl font-bold mb-4">No Top Rated Products Found</h3>
//         <p>We couldn't find any products with a 4+ star rating at the moment.</p>
//       </div>
//     );
//   }

//   return (
//     <section className="py-16 bg-gradient-to-br from-blue-50 to-indigo-50">
//       <div className="container mx-auto px-4">
//         <div className="text-center mb-12">
//           <div className="inline-block bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-1 rounded-full mb-3">
//             <span className="text-sm font-semibold">TOP RATED</span>
//           </div>
//           <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-3">
//             Customer <span className="text-blue-600">Favorites</span>
//           </h2>
//           <p className="text-lg text-gray-600 max-w-2xl mx-auto">
//             Discover our highest rated products loved by thousands of customers
//           </p>
//         </div>

//         <div className="px-4">
//           <Slider {...sliderSettings}>
//             {topRatedProducts.map((item, index) => (
//               <div key={item.product._id} className="px-2">
//                 <div
//                   className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 relative h-full"
//                   onClick={() => fetchProductDetails(item?.product?._id)}
//                 >
//                   {/* Premium badge for top products */}
//                   {index < 2 && (
//                     <div className="absolute top-4 left-4 z-10">
//                       <div className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-white px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide shadow-md">
//                         {index === 0 ? 'Best Seller' : 'Popular'}
//                       </div>
//                     </div>
//                   )}

//                   {/* Rating badge */}
//                   <div className="absolute top-4 right-4 z-10 bg-blue-600 text-white rounded-full w-12 h-12 flex flex-col items-center justify-center shadow-lg">
//                     <span className="text-lg font-bold">{item?.averageRating?.toFixed(1)}</span>
//                     <div className="flex -mt-1">
//                       <svg className="w-3 h-3 text-yellow-300" fill="currentColor" viewBox="0 0 20 20">
//                         <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
//                       </svg>
//                     </div>
//                   </div>

//                   <div className="relative h-64 overflow-hidden cursor-pointer">
//                     {item?.product?.variants?.[0]?.images?.[0] ? (
//                       <img
//                         src={item?.product?.variants?.[0]?.images?.[0]}
//                         alt={item?.product?.name}
//                         className="w-full h-full object-cover transition-transform duration-500 hover:scale-110 cursor-pointer"
//                         onError={(e) => {
//                           e.target.src = "../../../assets/brand-default.jpg";
//                           e.target.className = "w-full h-full object-contain p-4 bg-gray-100";
//                         }}
//                         loading='lazy'
//                       />
//                     ) : (
//                       <div className="w-full h-full bg-gray-100 flex items-center justify-center">
//                         <span className="text-gray-400">No Image Available</span>
//                       </div>
//                     )}
//                   </div>

//                   <div className="p-6">
//                     <div className="flex justify-between items-start mb-3">
//                       <div>
//                         <h3 className="text-xl font-bold text-gray-900 mb-1">{item?.product?.name}</h3>
//                         <div className="flex items-center">
//                           <div className="flex">
//                             {[...Array(5)].map((_, i) => (
//                               <svg
//                                 key={i}
//                                 className={`w-5 h-5 ${i < Math.floor(item?.averageRating) ? 'text-yellow-400' : 'text-gray-300'}`}
//                                 fill="currentColor"
//                                 viewBox="0 0 20 20"
//                               >
//                                 <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
//                               </svg>
//                             ))}
//                           </div>
//                           <span className="ml-2 text-sm text-gray-600">
//                             ({item?.ratingCount} review{item?.ratingCount !== 1 ? 's' : ''})
//                           </span>
//                         </div>
//                       </div>
//                       <div className="text-right">
//                         <span className="text-2xl font-bold text-gray-900">
//                           ₹{item?.product?.variants?.[0]?.price?.toLocaleString()}
//                         </span>
//                         {item?.product?.variants?.[0]?.mrp > item?.product?.variants?.[0]?.price && (
//                           <div className="text-xs text-gray-500 line-through">
//                             ₹{item?.product?.variants?.[0]?.mrp?.toLocaleString()}
//                           </div>
//                         )}
//                       </div>
//                     </div>

//                     <p className="text-gray-600 mb-5 line-clamp-2 h-12">
//                       {item.product.description}
//                     </p>

//                     <div className="flex justify-between items-center">
//                       {item?.product?.variants?.[0]?.stock < item?.product?.variants?.[0]?.stock_threshold ? (
//                         <div className="text-sm font-medium text-orange-600 bg-orange-50 px-3 py-1 rounded-full">
//                           Only {item?.product?.variants?.[0]?.stock} left!
//                         </div>
//                       ) : (
//                         <div className="text-sm font-medium text-green-600 bg-green-50 px-3 py-1 rounded-full">
//                           In Stock
//                         </div>
//                       )}

//                       {renderProductActions(item?.product)}
//                     </div>
//                   </div>
//                 </div>
//               </div>
//             ))}
//           </Slider>
//         </div>
//       </div>
//     </section>
//   );
// };

// export default TopRatedProducts;

// import React, { useEffect, useState } from 'react';
// import axios from 'axios';
// import Cookies from 'js-cookie';
// import { useNavigate } from 'react-router-dom';
// import { useDispatch, useSelector } from 'react-redux';
// import { updateItemQuantity, addToCart } from '../../../redux/thunks/cartThunks';

// const TopRatedProducts = () => {
//   const [products, setProducts] = useState([]);
//   const [filteredProducts, setFilteredProducts] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [activeFilter, setActiveFilter] = useState('all');
//   const navigate = useNavigate();
//   const dispatch = useDispatch();
//   const cartItems = useSelector(state => state.cart.items);

//   useEffect(() => {
//     const fetchTopRatedProducts = async () => {
//       try {
//         // const token = Cookies.get("ecom_user_token");
//         const response = await axios.get(
//           `${process.env.REACT_APP_BACKEND_URL}/api/seller/product/get-by-rating`,
//           // {
//           //   headers: {
//           //     Authorization: `Bearer ${token}`,
//           //   },
//           // }
//         );
//         setProducts(response?.data?.products);
//         setFilteredProducts(response?.data?.products?.filter(p => p.averageRating >= 4));
//         setLoading(false);
//       } catch (err) {
//         setError(err.message);
//         setLoading(false);
//         console.error("Error fetching top rated products:", err);
//       }
//     };

//     fetchTopRatedProducts();
//   }, []);

//   const fetchProductDetails = (productId) => {
//     navigate(`/product/${productId}`);
//   };

//   // Add to cart using thunk
//   const handleAddToCart = async (product) => {
//     if (product.stock <= 0) {
//       alert("This product is out of stock");
//       return;
//     }

//     try {
//       await dispatch(addToCart({
//         productId: product._id,
//         quantity: 1
//       }));
//     } catch (error) {
//       console.error("Add to cart failed:", error);
//       alert("Failed to add to cart. Please try again.");
//     }
//   };

//   // Update cart item quantity
//   const handleQuantityChange = async (productId, newQuantity) => {
//     try {
//       await dispatch(updateItemQuantity({ productId, quantity: newQuantity }));
//     } catch (error) {
//       console.error("Error updating quantity:", error);
//       alert("Failed to update quantity. Please try again.");
//     }
//   };

//   // Render product actions (add to cart or quantity controls)
//   const renderProductActions = (product) => {
//     const cartItem = cartItems.find(item => item.productId === product._id);

//     if (cartItem) {
//       return (
//         <div className="flex items-center justify-center space-x-2">
//           <button
//             className={`w-8 h-8 flex items-center justify-center rounded-full transition-all duration-200 shadow-sm ${
//               cartItem.quantity <= 0
//                 ? "bg-gray-100 text-gray-400 cursor-not-allowed"
//                 : "bg-gradient-to-b from-gray-100 to-gray-200 hover:from-gray-200 hover:to-gray-300 text-gray-700 hover:text-gray-900 hover:shadow-md"
//             }`}
//             onClick={(e) => {
//               e.stopPropagation();
//               const newQuantity = cartItem.quantity - 1;
//               handleQuantityChange(product._id, newQuantity);
//             }}
//             disabled={cartItem.quantity <= 0}
//           >
//             <span className="text-lg font-medium">−</span>
//           </button>
//           <span className="w-8 text-center font-medium text-gray-700">
//             {cartItem.quantity}
//           </span>
//           <button
//             className="w-8 h-8 flex items-center justify-center rounded-full bg-gradient-to-b from-blue-100 to-blue-200 hover:from-blue-200 hover:to-blue-300 text-blue-700 hover:text-blue-900 transition-all duration-200 shadow-sm hover:shadow-md"
//             onClick={(e) => {
//               e.stopPropagation();
//               handleQuantityChange(product._id, cartItem.quantity + 1);
//             }}
//           >
//             <span className="text-lg font-medium">+</span>
//           </button>
//         </div>
//       );
//     } else {
//       return (
//         <button
//           className="bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white px-5 py-2 rounded-full font-medium transition-all duration-300 transform hover:scale-105 shadow-md flex items-center"
//           onClick={(e) => {
//             e.stopPropagation();
//             handleAddToCart(product);
//           }}
//           disabled={product?.stock <= 0}
//         >
//           <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"></path>
//           </svg>
//           Add to Cart
//         </button>
//       );
//     }
//   };

//   const filterProducts = (filterType) => {
//     setActiveFilter(filterType);

//     if (filterType === 'all') {
//       setFilteredProducts(products);
//     } else if (filterType === '4plus') {
//       setFilteredProducts(products.filter(p => p.averageRating >= 4));
//     }
//   };

//   if (loading) {
//     return (
//       <div className="flex justify-center items-center h-96">
//         <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-600"></div>
//       </div>
//     );
//   }

//   if (error) {
//     return (
//       <div className="text-red-500 text-center p-10 bg-red-50 rounded-xl max-w-3xl mx-auto my-12">
//         <h3 className="text-xl font-bold mb-2">Error Loading Products</h3>
//         <p>{error}</p>
//         <p className="mt-4">Please try again later</p>
//       </div>
//     );
//   }

//   if (filteredProducts.length === 0) {
//     return (
//       <div className="text-gray-600 text-center p-10 bg-gray-50 rounded-xl max-w-3xl mx-auto my-12">
//         <h3 className="text-2xl font-bold mb-4">No Top Rated Products Found</h3>
//         <p>We couldn't find any products with a 4+ star rating at the moment.</p>
//       </div>
//     );
//   }

//   return (
//     <section className="py-16 bg-gradient-to-br from-blue-50 to-indigo-50">
//       <div className="container mx-auto px-4">
//         <div className="text-center mb-12">
//           <div className="inline-block bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-1 rounded-full mb-3">
//             <span className="text-sm font-semibold">PREMIUM SELECTION</span>
//           </div>
//           <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-3">
//             Customer <span className="text-blue-600">Favorites</span>
//           </h2>
//           <p className="text-lg text-gray-600 max-w-2xl mx-auto">
//             Discover our highest rated products loved by thousands of customers
//           </p>
//         </div>

//         <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
//           {filteredProducts.map((item, index) => (
//             <div
//               key={item.product._id}
//               className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 relative"
//               onClick={() => fetchProductDetails(item?.product?._id)}
//             >
//               {/* Premium badge for top products */}
//               {index < 2 && (
//                 <div className="absolute top-4 left-4 z-10">
//                   <div className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-white px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide shadow-md">
//                     {index === 0 ? 'Best Seller' : 'Popular'}
//                   </div>
//                 </div>
//               )}

//               {/* Rating badge */}
//               <div className="absolute top-4 right-4 z-10 bg-blue-600 text-white rounded-full w-12 h-12 flex flex-col items-center justify-center shadow-lg">
//                 <span className="text-lg font-bold">{item?.averageRating?.toFixed(1)}</span>
//                 <div className="flex -mt-1">
//                   <svg className="w-3 h-3 text-yellow-300" fill="currentColor" viewBox="0 0 20 20">
//                     <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
//                   </svg>
//                 </div>
//               </div>

//               <div className="relative h-64 overflow-hidden cursor-pointer">
//                 {item?.product?.images && item?.product?.images?.length > 0 ? (
//                   <img
//                     src={item?.product?.images[0]}
//                     alt={item?.product?.name}
//                     className="w-full h-full object-cover transition-transform duration-500 hover:scale-110 cursor-pointer"
//                     onError={(e) => {
//                       e.target.src = "../../../assets/brand-default.jpg";
//                       e.target.className = "w-full h-full object-contain p-4 bg-gray-100";
//                     }}
//                     loading='lazy'
//                   />
//                 ) : (
//                   <div className="w-full h-full bg-gray-100 flex items-center justify-center">
//                     <span className="text-gray-400">No Image Available</span>
//                   </div>
//                 )}
//               </div>

//               <div className="p-6">
//                 <div className="flex justify-between items-start mb-3">
//                   <div>
//                     <h3 className="text-xl font-bold text-gray-900 mb-1">{item?.product?.name}</h3>
//                     <div className="flex items-center">
//                       <div className="flex">
//                         {[...Array(5)].map((_, i) => (
//                           <svg
//                             key={i}
//                             className={`w-5 h-5 ${i < Math.floor(item?.averageRating) ? 'text-yellow-400' : 'text-gray-300'}`}
//                             fill="currentColor"
//                             viewBox="0 0 20 20"
//                           >
//                             <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
//                           </svg>
//                         ))}
//                       </div>
//                       <span className="ml-2 text-sm text-gray-600">
//                         ({item?.ratingCount} review{item?.ratingCount !== 1 ? 's' : ''})
//                       </span>
//                     </div>
//                   </div>
//                   <div className="text-right">
//                     <span className="text-2xl font-bold text-gray-900">₹{item?.product?.price?.toLocaleString()}</span>
//                     {item.product.price > 18000 && (
//                       <div className="text-xs text-gray-500 line-through">₹{(item?.product?.price * 1.1).toLocaleString()}</div>
//                     )}
//                   </div>
//                 </div>

//                 <p className="text-gray-600 mb-5 line-clamp-2 h-12">
//                   {item.product.description}
//                 </p>

//                 <div className="flex justify-between items-center">
//                   {item?.product?.stock < item?.product?.stock_threshold ? (
//                     <div className="text-sm font-medium text-orange-600 bg-orange-50 px-3 py-1 rounded-full">
//                       Only {item.product.stock} left!
//                     </div>
//                   ) : (
//                     <div className="text-sm font-medium text-green-600 bg-green-50 px-3 py-1 rounded-full">
//                       In Stock
//                     </div>
//                   )}

//                   {renderProductActions(item?.product)}
//                 </div>
//               </div>
//             </div>
//           ))}
//         </div>
//       </div>
//     </section>
//   );
// };

// export default TopRatedProducts;

// import React, { useEffect, useState } from 'react';
// import axios from 'axios';
// import Cookies from 'js-cookie';
// import { useNavigate } from 'react-router-dom';

// const TopRatedProducts = () => {
//   const [products, setProducts] = useState([]);
//   const [filteredProducts, setFilteredProducts] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [activeFilter, setActiveFilter] = useState('all');
//    const navigate = useNavigate()
//   useEffect(() => {
//     const fetchTopRatedProducts = async () => {
//       try {
//         const token = Cookies.get("ecom_user_token");
//         const response = await axios.get(
//           "https://seller-backend-29o9.onrender.com/api/seller/product/get-by-rating",
//           {
//             headers: {
//               Authorization: `Bearer ${token}`,
//             },
//           }
//         );
//         setProducts(response.data.products);
//         setFilteredProducts(response.data.products.filter(p => p.averageRating >= 4));
//         setLoading(false);
//       } catch (err) {
//         setError(err.message);
//         setLoading(false);
//         console.error("Error fetching top rated products:", err);
//       }
//     };

//     fetchTopRatedProducts();
//   }, []);

//     const fetchProductDetails = (productId) => {
//     navigate(`/product/${productId}`);
//   };

//   const handleAddToCart = async (product) => {
//       const token = Cookies.get("ecom_user_token");

//     if (product.stock <= 0) {
//         alert("This product is out of stock");
//         return;
//       }

//       try {
//         const response = await axios.post(
//           'https://seller-backend-29o9.onrender.com/api/buyer/add-to-cart',
//           {
//             productId: product._id,
//             quantity: 1
//           },
//           {
//             headers: {
//               Authorization: `Bearer ${token}`,
//               'Content-Type': 'application/json'
//             }
//           }
//         );
//         navigate('/cart');
//       } catch (error) {
//         console.error("Add to cart API failed:", error);
//         alert("Failed to add to cart. Please try again.");
//       }
//     };

//   const filterProducts = (filterType) => {
//     setActiveFilter(filterType);

//     if (filterType === 'all') {
//       setFilteredProducts(products);
//     } else if (filterType === '4plus') {
//       setFilteredProducts(products.filter(p => p.averageRating >= 4));
//     }
//   };

//   if (loading) {
//     return (
//       <div className="flex justify-center items-center h-96">
//         <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-600"></div>
//       </div>
//     );
//   }

//   if (error) {
//     return (
//       <div className="text-red-500 text-center p-10 bg-red-50 rounded-xl max-w-3xl mx-auto my-12">
//         <h3 className="text-xl font-bold mb-2">Error Loading Products</h3>
//         <p>{error}</p>
//         <p className="mt-4">Please try again later</p>
//       </div>
//     );
//   }

//   if (filteredProducts.length === 0) {
//     return (
//       <div className="text-gray-600 text-center p-10 bg-gray-50 rounded-xl max-w-3xl mx-auto my-12">
//         <h3 className="text-2xl font-bold mb-4">No Top Rated Products Found</h3>
//         <p>We couldn't find any products with a 4+ star rating at the moment.</p>
//       </div>
//     );
//   }

//   return (
//     <section className="py-16 bg-gradient-to-br from-blue-50 to-indigo-50">
//       <div className="container mx-auto px-4">
//         <div className="text-center mb-12">
//           <div className="inline-block bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-1 rounded-full mb-3">
//             <span className="text-sm font-semibold">PREMIUM SELECTION</span>
//           </div>
//           <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-3">
//             Customer <span className="text-blue-600">Favorites</span>
//           </h2>
//           <p className="text-lg text-gray-600 max-w-2xl mx-auto">
//             Discover our highest rated products loved by thousands of customers
//           </p>

//           <div className="flex justify-center mt-8 mb-6 space-x-4">
//             {/* <button
//               onClick={() => filterProducts('all')}
//               className={`px-5 py-2 rounded-full font-medium transition-all ${
//                 activeFilter === 'all'
//                   ? 'bg-blue-600 text-white shadow-lg'
//                   : 'bg-white text-gray-700 hover:bg-gray-100 shadow-md'
//               }`}
//             >
//               Show All
//             </button> */}
//             {/* <button
//               onClick={() => filterProducts('4plus')}
//               className={`px-5 py-2 rounded-full font-medium transition-all ${
//                 activeFilter === '4plus'
//                   ? 'bg-blue-600 text-white shadow-lg'
//                   : 'bg-white text-gray-700 hover:bg-gray-100 shadow-md'
//               }`}
//             >
//               <span className="flex items-center">
//                 <svg className="w-5 h-5 mr-1 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
//                   <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
//                 </svg>
//                 4+ Stars Only
//               </span>
//             </button> */}
//           </div>
//         </div>

//         <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
//           {filteredProducts.map((item, index) => (
//             <div
//               key={item.product._id}
//               className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 relative"
//               onClick={() => fetchProductDetails(item?.product._id)}
//             >
//               {/* Premium badge for top products */}
//               {index < 2 && (
//                 <div className="absolute top-4 left-4 z-10">
//                   <div className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-white px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide shadow-md">
//                     {index === 0 ? 'Best Seller' : 'Popular'}
//                   </div>
//                 </div>
//               )}

//               {/* Rating badge */}
//               <div className="absolute top-4 right-4 z-10 bg-blue-600 text-white rounded-full w-12 h-12 flex flex-col items-center justify-center shadow-lg">
//                 <span className="text-lg font-bold">{item.averageRating.toFixed(1)}</span>
//                 <div className="flex -mt-1">
//                   <svg className="w-3 h-3 text-yellow-300" fill="currentColor" viewBox="0 0 20 20">
//                     <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
//                   </svg>
//                 </div>
//               </div>

//               <div className="relative h-64 overflow-hidden cursor-pointer">
//                 {item.product.images && item.product.images.length > 0 ? (
//                   <img
//                     src={item.product.images[0]}
//                     alt={item.product.name}
//                     className="w-full h-full object-cover transition-transform duration-500 hover:scale-110 cursor-pointer"
//                     onError={(e) => {
//                       e.target.src = "../../../assets/brand-default.jpg";
//                       e.target.className = "w-full h-full object-contain p-4 bg-gray-100";
//                     }}
//                     loading='lazy'
//                   />
//                 ) : (
//                   <div className="w-full h-full bg-gray-100 flex items-center justify-center">
//                     <span className="text-gray-400">No Image Available</span>
//                   </div>
//                 )}
//               </div>

//               <div className="p-6">
//                 <div className="flex justify-between items-start mb-3">
//                   <div>
//                     <h3 className="text-xl font-bold text-gray-900 mb-1">{item.product.name}</h3>
//                     <div className="flex items-center">
//                       <div className="flex">
//                         {[...Array(5)].map((_, i) => (
//                           <svg
//                             key={i}
//                             className={`w-5 h-5 ${i < Math.floor(item.averageRating) ? 'text-yellow-400' : 'text-gray-300'}`}
//                             fill="currentColor"
//                             viewBox="0 0 20 20"
//                           >
//                             <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
//                           </svg>
//                         ))}
//                       </div>
//                       <span className="ml-2 text-sm text-gray-600">
//                         ({item.ratingCount} review{item.ratingCount !== 1 ? 's' : ''})
//                       </span>
//                     </div>
//                   </div>
//                   <div className="text-right">
//                     <span className="text-2xl font-bold text-gray-900">₹{item.product.price.toLocaleString()}</span>
//                     {item.product.price > 18000 && (
//                       <div className="text-xs text-gray-500 line-through">₹{(item.product.price * 1.1).toLocaleString()}</div>
//                     )}
//                   </div>
//                 </div>

//                 <p className="text-gray-600 mb-5 line-clamp-2 h-12">
//                   {item.product.description}
//                 </p>

//                 <div className="flex justify-between items-center">
//                   {item.product.stock < item.product.stock_threshold ? (
//                     <div className="text-sm font-medium text-orange-600 bg-orange-50 px-3 py-1 rounded-full">
//                       Only {item.product.stock} left!
//                     </div>
//                   ) : (
//                     <div className="text-sm font-medium text-green-600 bg-green-50 px-3 py-1 rounded-full">
//                       In Stock
//                     </div>
//                   )}

//                   <button className="bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white px-5 py-2 rounded-full font-medium transition-all duration-300 transform hover:scale-105 shadow-md flex items-center" onClick={(e) => {
//                         e.stopPropagation();
//                         handleAddToCart(item?.product);
//                       }} >
//                     <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"></path>
//                     </svg>
//                     Add to Cart
//                   </button>
//                 </div>
//               </div>
//             </div>
//           ))}
//         </div>

//         {/* <div className="text-center mt-14">
//           <button className="px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-700 text-white rounded-full font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 inline-flex items-center">
//             Explore All Premium Products
//             <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
//             </svg>
//           </button>
//         </div> */}
//       </div>
//     </section>
//   );
// };

// export default TopRatedProducts;

// import React, { useEffect, useState } from 'react';
// import axios from 'axios';
// import Cookies from 'js-cookie';

// const TopRatedProducts = () => {
//   const [products, setProducts] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);

//   useEffect(() => {
//     const fetchTopRatedProducts = async () => {
//       try {
//         const token = Cookies.get("ecom_user_token");
//         const response = await axios.get(
//           "https://seller-backend-29o9.onrender.com/api/seller/product/get-by-rating",
//           {
//             headers: {
//               Authorization: `Bearer ${token}`,
//             },
//           }
//         );
//         setProducts(response.data.products);
//         setLoading(false);
//       } catch (err) {
//         setError(err.message);
//         setLoading(false);
//         console.error("Error fetching top rated products:", err);
//       }
//     };

//     fetchTopRatedProducts();
//   }, []);

//   if (loading) {
//     return (
//       <div className="flex justify-center items-center h-64">
//         <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
//       </div>
//     );
//   }

//   if (error) {
//     return (
//       <div className="text-red-500 text-center p-4">
//         Error loading top rated products: {error}
//       </div>
//     );
//   }

//   if (products.length === 0) {
//     return (
//       <div className="text-gray-500 text-center p-4">
//         No top rated products available at the moment.
//       </div>
//     );
//   }

//   return (
//     <section className="py-12 bg-gradient-to-b from-gray-50 to-white">
//       <div className="container mx-auto px-4">
//         <h2 className="text-3xl font-bold text-center mb-2 text-gray-800">
//           Top Rated Products
//         </h2>
//         <p className="text-center text-gray-600 mb-8 max-w-2xl mx-auto">
//           Discover our most loved products based on customer ratings
//         </p>

//         <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
//           {products.map((item, index) => (
//             <div
//               key={item.product._id}
//               className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-shadow duration-300"
//             >
//               <div className="relative">
//                 {item.product.images && item.product.images.length > 0 ? (
//                   <img
//                     src={item.product.images[0]}
//                     alt={item.product.name}
//                     className="w-full h-48 object-cover"
//                     onError={(e) => {
//                       e.target.src = "https://via.placeholder.com/300x200?text=Product+Image";
//                     }}
//                   />
//                 ) : (
//                   <div className="w-full h-48 bg-gray-200 flex items-center justify-center">
//                     <span className="text-gray-500">No Image Available</span>
//                   </div>
//                 )}
//                 <div className="absolute top-2 left-2 bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded-full">
//                   {index + 1 === 1 ? 'BEST SELLER' : `TOP ${index + 1}`}
//                 </div>
//               </div>

//               <div className="p-6">
//                 <h3 className="text-xl font-semibold text-gray-800 mb-2">
//                   {item.product.name}
//                 </h3>

//                 <div className="flex items-center mb-3">
//                   <div className="flex">
//                     {[...Array(5)].map((_, i) => (
//                       <svg
//                         key={i}
//                         className={`w-5 h-5 ${i < Math.floor(item.averageRating) ? 'text-yellow-400' : 'text-gray-300'}`}
//                         fill="currentColor"
//                         viewBox="0 0 20 20"
//                       >
//                         <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
//                       </svg>
//                     ))}
//                   </div>
//                   <span className="ml-2 text-sm text-gray-600">
//                     {item.averageRating.toFixed(1)} ({item.ratingCount} review{item.ratingCount !== 1 ? 's' : ''})
//                   </span>
//                 </div>

//                 <p className="text-gray-600 text-sm mb-4 line-clamp-2">
//                   {item.product.description}
//                 </p>

//                 <div className="flex justify-between items-center">
//                   <span className="text-lg font-bold text-gray-900">
//                     ₹{item.product.price.toLocaleString()}
//                   </span>
//                   <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors duration-300">
//                     Add to Cart
//                   </button>
//                 </div>

//                 {item.product.stock < item.product.stock_threshold && (
//                   <div className="mt-3 text-sm text-orange-600">
//                     Only {item.product.stock} left in stock!
//                   </div>
//                 )}
//               </div>
//             </div>
//           ))}
//         </div>

//         <div className="text-center mt-10">
//           <button className="px-6 py-3 bg-white border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors duration-300 font-medium">
//             View All Top Rated Products
//           </button>
//         </div>
//       </div>
//     </section>
//   );
// };

// export default TopRatedProducts;
